import { supabase } from '../supabase/config';
import { isMobile } from '../utils/helpers';

// Helper function to escape HTML
export const escapeHtml = (text) => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

// Format message timestamp
export const formatMessageTime = (timestamp) => {
  if (!timestamp) return '';
  
  const date = new Date(timestamp);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return '';
  }
  
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

// Format date divider
export const formatDateDivider = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return '';
  }
  
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  // Reset time to compare only dates
  today.setHours(0, 0, 0, 0);
  yesterday.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  
  if (date.getTime() === today.getTime()) {
    return 'Today';
  } else if (date.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
};

// Check if message is exactly one emoji
export const isOnlyEmoji = (text) => {
  if (!text || text.trim().length === 0) return false;
  
  const trimmed = text.trim();
  
  // Common single emoji ranges
  // This will match most emojis but not multiple emojis or emoji + text
  const singleEmojiRegex = /^(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|[\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|[\ud83c[\ude32-\ude3a]|[\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])$/;
  
  // Also check length - single emoji should be 1-7 characters max
  // Multiple emojis will be longer
  if (trimmed.length > 7) return false;
  
  // Count how many emojis using spread operator
  // This properly counts grapheme clusters (emojis)
  const emojiCount = [...trimmed].filter(char => {
    const code = char.codePointAt(0);
    return code > 0x1F000; // Most emojis are above this code point
  }).length;
  
  // Strict: the message is emoji-only ONLY if, after removing every emoji,
  // modifier, ZWJ, variation selector, skin tone and whitespace, nothing
  // remains. This prevents short text+emoji (e.g. "hi😀") from being treated
  // as emoji-only and blown up.
  void emojiCount; void singleEmojiRegex; // legacy heuristics no longer used
  try {
    if (!/\p{Extended_Pictographic}/u.test(trimmed)) return false;
    const stripped = trimmed.replace(
      /[\p{Extended_Pictographic}\p{Emoji_Modifier}‍️\u{1F3FB}-\u{1F3FF}\s]/gu,
      ''
    );
    return stripped.length === 0;
  } catch {
    return false;
  }
};

// Long press detection
let longPressTimer = null;
let longPressTriggered = false;

export const handleMessageTouchStart = (event, messageId) => {
  if (!isMobile) return;

  longPressTriggered = false;

  // Start long press timer (500ms)
  longPressTimer = setTimeout(() => {
    longPressTriggered = true;
    window.handleMessageLongPress(messageId);
  }, 500);
};

export const handleMessageTouchEnd = (event, messageId) => {
  if (!isMobile) return;
  
  // Clear the timer
  clearTimeout(longPressTimer);
  
  // If long press wasn't triggered, treat as normal click
  if (!longPressTriggered) {
    window.toggleMessageTimestamp(messageId);
  }
};

export const handleMessageClick = (event, messageId) => {
  // Desktop behavior - normal click
  if (!isMobile) {
    window.toggleMessageTimestamp(messageId);
  }
  // Mobile clicks are handled by touch events
};

// Show reactions details panel
export const showReactionsDetails = async (messageId, emoji = null) => {
  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  
  // Get message data
  const messageData = window.currentChatMessages?.find(m => m.id === messageId);
  if (!messageData || !messageData.reactions) return;
  
  // Build user cache from current messages if not already available
  if (!window.eventParticipants) {
    window.eventParticipants = [];
    
    // Extract unique users from current chat messages
    const userMap = new Map();
    if (window.currentChatMessages) {
      window.currentChatMessages.forEach(msg => {
        if (!userMap.has(msg.user_id)) {
          userMap.set(msg.user_id, {
            user_id: msg.user_id,
            user_name: msg.user_name,
            user_avatar: msg.user_avatar
          });
        }
      });
    }
    
    window.eventParticipants = Array.from(userMap.values());
  }
  
  // Find the chat container - try multiple selectors
  const chatPanel = 
    document.getElementById('eventChatPanel') ||
    document.getElementById('chatPanel') ||
    document.querySelector('.chat-panel') ||
    document.querySelector('[class*="chat"]') ||
    document.querySelector('#messagesContainer')?.closest('div[style*="position: fixed"]') ||
    document.querySelector('div[style*="position: fixed"][style*="right"]');

  // Use body as fallback if chat panel not found
  const container = chatPanel || document.body;
  const useAbsolute = !!chatPanel;
  
  // Remove existing panel
  const existingPanel = document.getElementById('reactionsDetailsPanel');
  if (existingPanel) existingPanel.remove();
  
  // Create panel
  const panel = document.createElement('div');
  panel.id = 'reactionsDetailsPanel';
  panel.style.cssText = `
    position: ${useAbsolute ? 'absolute' : 'fixed'};
    margin: 0 10px;
    bottom: -100%;
    left: ${useAbsolute ? '0' : '50%'};
    right: ${useAbsolute ? '0' : 'auto'};
    ${!useAbsolute ? 'transform: translateX(-50%); width: 90%; max-width: 400px;' : ''}
    background: white;
    border-radius: 12px 12px 0 0;
    box-shadow: 0 -2px 16px rgba(0,0,0,0.15);
    max-height: 40vh;
    overflow-y: auto;
    z-index: ${useAbsolute ? '1001' : '10000'};
    transition: bottom 0.3s ease-out;
  `;
  
  // Build reactions list
  let reactionsHTML = '<div style="padding: 16px;">';
  
  // Header
  reactionsHTML += `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; border-bottom: 1px solid #e8eaed; padding-bottom: 12px;">
      <h3 style="margin: 0; font-size: 16px; font-weight: 600; color: #202124;">Reactions</h3>
      <button onclick="window.closeReactionsDetails()" style="
        background: none;
        border: none;
        font-size: 20px;
        cursor: pointer;
        color: #5f6368;
        padding: 0;
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: background 0.2s;
      " onmouseover="this.style.background='#f1f3f4'" onmouseout="this.style.background='transparent'">×</button>
    </div>
  `;
  
  // Filter reactions (show specific emoji or all)
  const reactionsToShow = emoji 
    ? { [emoji]: messageData.reactions[emoji] }
    : messageData.reactions;
  
  // For each user who reacted
  const allReactors = [];
  for (const [reactionEmoji, userIds] of Object.entries(reactionsToShow)) {
    if (!userIds || userIds.length === 0) continue;
    
    for (const userId of userIds) {
      allReactors.push({ userId, emoji: reactionEmoji });
    }
  }
  
  // Show each reactor in compact format
  for (const { userId, emoji: reactionEmoji } of allReactors) {
    const userData = window.eventParticipants?.find(p => p.user_id === userId);
    const userName = userData?.user_name || 'Unknown User';
    const userAvatar = userData?.user_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=4285f4&color=fff&size=32`;
    
    const isCurrentUser = userId === currentUserId;
    
    reactionsHTML += `
      <div style="
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 8px 4px;
        border-radius: 8px;
        transition: background 0.2s;
      " onmouseover="this.style.background='#f1f3f4'" onmouseout="this.style.background='transparent'">
        <img src="${userAvatar}" style="
          width: 32px;
          height: 32px;
          border-radius: 50%;
          object-fit: cover;
          flex-shrink: 0;
        " onerror="this.src='${userAvatar}'">
        <div style="
          flex: 1;
          font-size: 14px;
          font-weight: 500;
          color: #202124;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        ">
          ${escapeHtml(userName)}${isCurrentUser ? ' <span style="color: #5f6368; font-weight: 400;">(You)</span>' : ''}
        </div>
        <div style="font-size: 20px; flex-shrink: 0;">${reactionEmoji}</div>
      </div>
    `;
  }
  
  reactionsHTML += '</div>';
  panel.innerHTML = reactionsHTML;
  
  // Append to container
  container.appendChild(panel);
  
  // Slide up animation
  requestAnimationFrame(() => {
    panel.style.bottom = '0';
  });
  
  // Create backdrop
  const backdrop = document.createElement('div');
  backdrop.id = 'reactionsDetailsBackdrop';
  backdrop.style.cssText = `
    position: ${useAbsolute ? 'absolute' : 'fixed'};
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,${useAbsolute ? '0.3' : '0.4'});
    z-index: ${useAbsolute ? '1000' : '9999'};
    opacity: 0;
    transition: opacity 0.3s;
  `;
  backdrop.onclick = () => window.closeReactionsDetails();
  
  container.appendChild(backdrop);
  
  requestAnimationFrame(() => {
    backdrop.style.opacity = '1';
  });
};

// Close reactions details panel
export const closeReactionsDetails = () => {
  const panel = document.getElementById('reactionsDetailsPanel');
  const backdrop = document.getElementById('reactionsDetailsBackdrop');
  
  if (panel) {
    panel.style.bottom = '-100%';
    setTimeout(() => panel.remove(), 300);
  }
  
  if (backdrop) {
    backdrop.style.opacity = '0';
    setTimeout(() => backdrop.remove(), 300);
  }
};

export const handleMessageLongPress = (messageId) => {
  if (window.currentChatIsArchived) return;
  const messageEl = document.querySelector(`[data-message-id="${messageId}"]`);
  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  const userId = messageEl?.getAttribute('data-user-id');
  const isOwnMessage = userId === currentUserId;
  
  if (isOwnMessage) {
    // Show delete button
    window.showDeleteMessageMenu(messageId);
  } else {
    // Show emoji reaction picker
    window.showEmojiReactionPicker(messageId);
  }
};

export const showDeleteMessageMenu = (messageId, e) => {
  // Remove any existing menu
  const existingMenu = document.getElementById('messageActionsMenu');
  if (existingMenu) existingMenu.remove();

  // Position menu relative to the button using fixed positioning to avoid overflow clipping
  const btn = e?.target?.closest('button') || e?.currentTarget;
  const btnRect = btn?.getBoundingClientRect();
  const menuWidth = 180;
  const menuHeight = 100;
  const viewportH = window.innerHeight;

  const top = btnRect
    ? (btnRect.bottom + menuHeight > viewportH ? btnRect.top - menuHeight - 4 : btnRect.bottom + 4)
    : 200;
  const left = btnRect
    ? Math.max(8, Math.min(btnRect.right - menuWidth, window.innerWidth - menuWidth - 8))
    : 100;

  // Create menu
  const menu = document.createElement('div');
  menu.id = 'messageActionsMenu';
  menu.style.cssText = `
    position: fixed;
    top: ${top}px;
    left: ${left}px;
    background: white;
    border: 1px solid #e8eaed;
    border-radius: 8px;
    padding: 4px 0;
    min-width: ${menuWidth}px;
    z-index: 99999;
    box-shadow: 0 2px 10px rgba(0,0,0,0.15);
  `;
  
  // Edit option
  const editOption = document.createElement('div');
  editOption.textContent = 'Edit';
  editOption.style.cssText = `
    padding: 8px 16px;
    cursor: pointer;
    font-size: 14px;
    color: #5f6368;
    transition: background 0.2s;
    white-space: nowrap;
  `;
  
  editOption.onmouseover = () => editOption.style.background = '#f1f3f4';
  editOption.onmouseout = () => editOption.style.background = 'transparent';
  
  editOption.onclick = () => {
    window.startEditingMessage(messageId);
    menu.remove();
  };
  
  menu.appendChild(editOption);
  
// Delete option with warning
const deleteContainer = document.createElement('div');
deleteContainer.style.cssText = `
  cursor: pointer;
  transition: background 0.2s;
`;

const deleteOption = document.createElement('div');
deleteOption.textContent = 'Delete';
deleteOption.style.cssText = `
  padding: 8px 16px 4px 16px;
  font-size: 14px;
  color: #ea4335;
  white-space: nowrap;
`;

const warningText = document.createElement('div');
warningText.textContent = '(visible to everyone)';
warningText.style.cssText = `
  padding: 0 16px 8px 16px;
  font-size: 11px;
  color: #5f6368;
  font-style: italic;
`;

// Apply hover to container only
deleteContainer.onmouseover = () => {
  deleteContainer.style.background = '#f1f3f4';
};
deleteContainer.onmouseout = () => {
  deleteContainer.style.background = 'transparent';
};

deleteContainer.onclick = () => {
  window.deleteMessage(messageId);
  menu.remove();
};

deleteContainer.appendChild(deleteOption);
deleteContainer.appendChild(warningText);
menu.appendChild(deleteContainer);
  
  document.body.appendChild(menu);

  // Remove menu when clicking elsewhere
  setTimeout(() => {
    document.addEventListener('click', function removeMenu(ev) {
      if (!menu.contains(ev.target) && !ev.target.closest('.message-hover-actions')) {
        menu.remove();
        document.removeEventListener('click', removeMenu);
      }
    });
  }, 100);
};

export const startEditingMessage = (messageId) => {
  // 🆕 If already editing another message, cancel that first
  if (window.editingMessageId && window.editingMessageId !== messageId) {
    window.cancelEditingMessage(window.editingMessageId);
  }
  
  // Get message data
  const messageData = window.currentChatMessages?.find(m => m.id === messageId);
  if (!messageData) {
    return;
  }
  
  // Store editing state
  window.editingMessageId = messageId;
  window.originalMessageText = messageData.message;
  
  // Get message input
  const messageInput = document.getElementById('messageInput');
  if (!messageInput) return;
  
  // Set input value to current message text
  messageInput.value = messageData.message;
  messageInput.focus();
  
  // Get send button container
  const sendButton = document.getElementById('sendMessageButton') || 
                     document.querySelector('button[onclick*="sendMessage"]');
  
  if (sendButton) {
    // Hide send button, show edit controls
    sendButton.style.display = 'none';
    
    // 🆕 Remove any existing edit controls first
    const existingControls = document.getElementById('editMessageControls');
    if (existingControls) {
      existingControls.remove();
    }
    
    // Create edit controls container
    const editControls = document.createElement('div');
    editControls.id = 'editMessageControls';
    editControls.setAttribute('data-editing-message-id', messageId);
    editControls.style.cssText = `
      display: flex;
      gap: 8px;
      align-items: center;
    `;
    
    // Cancel button (X)
    const cancelBtn = document.createElement('button');
    cancelBtn.innerHTML = '✕';
    cancelBtn.style.cssText = `
      background: #f1f3f4;
      border: none;
      border-radius: 50%;
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-size: 18px;
      color: #5f6368;
      transition: background 0.2s;
    `;
    cancelBtn.onmouseover = () => cancelBtn.style.background = '#e8eaed';
    cancelBtn.onmouseout = () => cancelBtn.style.background = '#f1f3f4';
    cancelBtn.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const storedMessageId = editControls.getAttribute('data-editing-message-id');
      console.log('Cancel button clicked, stored ID:', storedMessageId);
      window.cancelEditingMessage(storedMessageId);
    };
    
    // Confirm button (checkmark)
    const confirmBtn = document.createElement('button');
    confirmBtn.innerHTML = '✓';
    confirmBtn.style.cssText = `
      background: #4285f4;
      border: none;
      border-radius: 50%;
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-size: 18px;
      color: white;
      transition: background 0.2s;
    `;
    confirmBtn.onmouseover = () => confirmBtn.style.background = '#1a73e8';
    confirmBtn.onmouseout = () => confirmBtn.style.background = '#4285f4';
    confirmBtn.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const storedMessageId = editControls.getAttribute('data-editing-message-id');
      window.confirmEditingMessage(storedMessageId);
    };
    
    editControls.appendChild(cancelBtn);
    editControls.appendChild(confirmBtn);
    
    // Insert edit controls where send button was
    sendButton.parentElement.appendChild(editControls);
  }
  
  // Add visual indicator on the message being edited
  const messageBubble = document.querySelector(`[data-message-id="${messageId}"]`);
  if (messageBubble) {
    messageBubble.style.outline = '2px solid #4285f4';
    messageBubble.style.outlineOffset = '2px';
  }
};

export const cancelEditingMessage = (messageId) => {
  // Use passed messageId or fallback to global
  const idToCancel = messageId || window.editingMessageId;
  
  // Remove outline from message
  if (idToCancel) {
    const messageBubble = document.querySelector(`[data-message-id="${idToCancel}"]`);
    
    if (messageBubble) {
      messageBubble.style.outline = 'none';
      messageBubble.style.outlineOffset = '0';
      console.log('✅ Removed outline');
    }
  }
  
  // Clear input
  const messageInput = document.getElementById('messageInput');
  if (messageInput) {
    messageInput.value = '';
  }
  
  // Remove edit controls
  const editControls = document.getElementById('editMessageControls');
  if (editControls) {
    editControls.remove();
  }
  
  // Show send button again
  const sendButton = document.getElementById('sendMessageButton') || 
                     document.querySelector('button[onclick*="sendMessage"]');
  if (sendButton) {
    sendButton.style.display = 'flex';
  }
  
  // Clear editing state
  window.editingMessageId = null;
  window.originalMessageText = null;
};

export const confirmEditingMessage = async (messageId) => {
    // Use passed messageId or fallback to global
  const idToConfirm = messageId || window.editingMessageId;
  
  if (!idToConfirm) return;
  
  if (!window.editingMessageId) return;
  
  const messageInput = document.getElementById('messageInput');
  if (!messageInput) return;
  
  const newText = messageInput.value.trim();
  
  // Don't save if empty or unchanged
  if (!newText) {
    window.showToast('Message cannot be empty', 'error');
    return;
  }
  
  if (newText === window.originalMessageText) {
    window.cancelEditingMessage();
    return;
  }
  
  try {
    const supabaseClient = window.supabase || supabase;
    
    // Update message in database
    const { error } = await supabaseClient
      .from('messages')
      .update({ 
        message: newText,
        edited_at: new Date().toISOString()
      })
      .eq('id', window.editingMessageId);
    
    if (error) {
      window.showToast('Failed to update message', 'error');
      return;
    }

    // Update UI immediately (optimistic update)
    const messageBubble = document.querySelector(`[data-message-id="${window.editingMessageId}"]`);
    if (messageBubble) {
      const messageText = messageBubble.querySelector('p');
      if (messageText) {
        messageText.textContent = newText;
      }
      
// Add "edited" indicator
let editedIndicator = messageBubble.querySelector('.edited-indicator');
if (!editedIndicator) {
  editedIndicator = document.createElement('span');
  editedIndicator.className = 'edited-indicator';
  editedIndicator.textContent = ' (edited)';
  
  // 🆕 Check if this is own message for proper color
  const isOwnMessage = messageBubble.classList.contains('message-bubble-own');
  
  editedIndicator.style.cssText = `
    font-size: 11px;
    color: ${isOwnMessage ? 'rgba(255,255,255,0.7)' : '#5f6368'};
    margin-left: 6px;
    font-style: italic;
  `;
  
  const messageText = messageBubble.querySelector('p');
  if (messageText) {
    messageText.appendChild(editedIndicator);
  }
}
    }
    
    // Update local message data
    if (window.currentChatMessages) {
      const msgIndex = window.currentChatMessages.findIndex(m => m.id === window.editingMessageId);
      if (msgIndex !== -1) {
        window.currentChatMessages[msgIndex].message = newText;
        window.currentChatMessages[msgIndex].edited_at = new Date().toISOString();
      }
    }

    // Remove outline
if (messageBubble) {
  messageBubble.style.outline = 'none';
  messageBubble.style.outlineOffset = '0';
}
    
    // Exit editing mode
    window.cancelEditingMessage();
    
  } catch (error) {
    window.showToast('Failed to update message', 'error');
  }
};

// Emoji picker for others' messages
export const showEmojiReactionPicker = (messageId) => {
  // Remove any existing picker
  const existingPicker = document.getElementById('emojiReactionPicker');
  if (existingPicker) existingPicker.remove();
  
  const messageEl = document.querySelector(`[data-message-id="${messageId}"]`);
  if (!messageEl) return;
  
  const messageBubble = messageEl.closest('.message-bubble');
  if (!messageBubble) return;
  
  // Create emoji picker
  const picker = document.createElement('div');
  picker.id = 'emojiReactionPicker';
  picker.style.cssText = `
    position: absolute;
    bottom: calc(100% + 5px);
    left: 0;
    background: white;
    border: 1px solid #e8eaed;
    border-radius: 20px;
    padding: 6px 8px;
    display: flex;
    gap: 4px;
    z-index: 1000;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  `;
  
  const emojis = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
  
  emojis.forEach(emoji => {
    const btn = document.createElement('button');
    btn.textContent = emoji;
    btn.style.cssText = `
      background: none;
      border: none;
      font-size: 20px;
      cursor: pointer;
      padding: 4px;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: transform 0.15s, background 0.15s;
    `;
    
    btn.onmouseover = () => {
      btn.style.transform = 'scale(1.2)';
      btn.style.background = '#f1f3f4';
    };
    btn.onmouseout = () => {
      btn.style.transform = 'scale(1)';
      btn.style.background = 'none';
    };
    
    btn.onclick = (e) => {
      e.stopPropagation();
      window.addReaction(messageId, emoji);
      picker.remove();
    };
    
    picker.appendChild(btn);
  });
  
  messageBubble.style.position = 'relative';
  messageBubble.appendChild(picker);
  
  // Remove picker when clicking elsewhere
  setTimeout(() => {
    document.addEventListener('click', function removePicker(e) {
      if (!picker.contains(e.target)) {
        picker.remove();
        document.removeEventListener('click', removePicker);
      }
    });
  }, 100);
};
// Create message element
export const createMessageElement = (message, isOwnMessage, isLastInGroup = true, isFirstInGroup = true, showTimestamp = false) => {
  const messageDiv = document.createElement('div');
  messageDiv.style.cssText = `
    display: flex;
    flex-direction: ${isOwnMessage ? 'row-reverse' : 'row'};
    gap: 8px;
    align-items: flex-start;
    animation: messageSlideIn 0.2s ease-out;
    margin-bottom: ${isLastInGroup ? '12px' : '2px'};
  `;

    // 🆕 Check if message is deleted
  const isDeleted = message.is_deleted;
  
  const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(message.user_name)}&background=4285f4&color=fff&size=32`;
  
// NEVER show status icon on initial render - will be added by UPDATE handler
let statusIcon = '';

  messageDiv.innerHTML = `
<style>
  @keyframes messageSlideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .message-bubble {
    cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s;
  }
  
  .message-bubble:hover {
    box-shadow: 0 2px 4px rgba(0,0,0,0.15) !important;
  }
  
  .message-bubble-other:hover {
    background: #f5f5f5 !important;
  }
  
  .message-bubble-own:hover {
    background: #1a73e8 !important;
  }
  
  /* Show hover actions when hovering over message wrapper */
  .message-wrapper:hover .message-hover-actions {
    opacity: 1 !important;
  }
  
  .avatar-clickable {
    cursor: pointer;
    transition: transform 0.2s;
  }
  
  .avatar-clickable:hover {
    transform: scale(1.1);
  }
</style>
    
<!-- Avatar (only show on last message in group) -->
${!isOwnMessage && isLastInGroup ? `
      <div style="position: relative; flex-shrink: 0;">
        <img 
          src="${message.user_avatar || fallbackAvatar}"
          alt="${message.user_name}"
          class="avatar-clickable"
          onclick="window.showUserProfile('${message.user_id}')"
          style="
            width: 32px;
            height: 32px;
            border-radius: 50%;
            object-fit: cover;
            border: 2px solid var(--status-color-${message.user_id}, #9e9e9e);
          "
          onerror="this.src='${fallbackAvatar}'"
          title="${escapeHtml(message.user_name)}"
        />
        <div style="
          position: absolute;
          bottom: 0;
          right: 0;
          width: 10px;
          height: 10px;
          background: var(--status-color-${message.user_id}, #9e9e9e);
          border: 2px solid white;
          border-radius: 50%;
        "></div>
      </div>
    ` : (!isOwnMessage ? '<div style="width: 32px; flex-shrink: 0;"></div>' : '')}
    
<!-- Message Content -->
<div class="message-wrapper" style="
  max-width: 70%;
  display: flex;
  flex-direction: column;
  gap: 0;
  align-items: ${isOwnMessage ? 'flex-end' : 'flex-start'};
  position: relative;
">
  ${isOnlyEmoji(message.message) && !message.is_deleted ? `
    <!-- Just the emoji, no bubble -->
    <div 
      data-message-id="${message.id}"
      data-user-id="${message.user_id}"
      onclick="window.handleMessageClick(event, '${message.id}')"
      ${isMobile ? `
        ontouchstart="window.handleMessageTouchStart(event, '${message.id}')"
        ontouchend="window.handleMessageTouchEnd(event, '${message.id}')"
        oncontextmenu="return false;"
      ` : ''}
      style="
        font-size: 32px;
        line-height: 1.2;
        cursor: pointer;
        user-select: none;
        -webkit-user-select: none;
      "
    >
      ${message.message}
      
      <!-- Emoji reactions container -->
      <div id="reactions_${message.id}" style="
        display: flex;
        gap: 4px;
        flex-wrap: wrap;
        margin-top: 8px;
        font-size: 14px;
      "></div>
    </div>
  ` : `
    <!-- Regular message bubble -->
    <div 
      data-message-id="${message.id}"
      data-user-id="${message.user_id}"
      class="message-bubble ${message.is_deleted ? '' : (isOwnMessage ? 'message-bubble-own' : 'message-bubble-other')}"
      ${!message.is_deleted ? `
        onclick="window.handleMessageClick(event, '${message.id}')"
        ${isMobile ? `
          ontouchstart="window.handleMessageTouchStart(event, '${message.id}')"
          ontouchend="window.handleMessageTouchEnd(event, '${message.id}')"
          oncontextmenu="return false;"
        ` : ''}
      ` : ''}
      style="
        background: ${message.is_deleted ? '#f1f3f4' : (isOwnMessage ? '#4285f4' : 'white')};
        color: ${message.is_deleted ? '#9e9e9e' : (isOwnMessage ? 'white' : '#202124')};
        padding: 8px 12px;
        border-radius: ${isOwnMessage ? '16px 16px 4px 16px' : '16px 16px 16px 4px'};
        word-wrap: break-word;
        box-shadow: 0 1px 2px rgba(0,0,0,0.1);
        position: relative;
        overflow-wrap: break-word;
        word-break: break-word;
        cursor: ${message.is_deleted ? 'default' : 'pointer'};
        font-style: ${message.is_deleted ? 'italic' : 'normal'};
        user-select: none;
        -webkit-user-select: none;
      "
    >
      <p style="
        margin: 0;
        font-size: 14px;
        line-height: 1.4;
        white-space: pre-wrap;
        word-break: break-word;
        user-select: none;
        -webkit-user-select: none;
        overflow-wrap: break-word;
      ">${message.is_deleted ? 'Message deleted' : escapeHtml(message.message)}${!message.is_deleted && message.edited_at ? `<span class="edited-indicator" style="font-size: 11px; color: ${isOwnMessage ? 'rgba(255,255,255,0.7)' : '#5f6368'}; margin-left: 6px; font-style: italic;">(edited)</span>` : ''}</p>
      
      ${!message.is_deleted ? `
        <!-- Emoji reactions container (only if not deleted) -->
        <div id="reactions_${message.id}" style="
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
          margin-top: 6px;
        "></div>
        
<!-- Hover actions INSIDE the bubble (desktop only, only if not deleted) -->
        ${!isMobile && !isOwnMessage && !window.currentChatIsArchived ? `
          <div class="message-hover-actions" style="
            position: absolute;
            top: 50%;
            right: -35px;
            transform: translateY(-50%);
            opacity: 0;
            transition: opacity 0.2s;
            pointer-events: none;
          ">
            <button onclick="event.stopPropagation(); window.showEmojiReactionPicker('${message.id}')" style="
              background: white;
              border: 1px solid #e8eaed;
              border-radius: 50%;
              width: 28px;
              height: 28px;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              font-size: 16px;
              padding: 0;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
              pointer-events: auto;
              transition: transform 0.2s;
            " 
            onmouseover="this.style.transform='scale(1.15)'"
            onmouseout="this.style.transform='scale(1)'"
            title="React">
              😊
            </button>
          </div>
        ` : ''}
        
${!isMobile && isOwnMessage && !window.currentChatIsArchived ? `
          <div class="message-hover-actions" style="
            position: absolute;
            top: 50%;
            left: -35px;
            transform: translateY(-50%);
            opacity: 0;
            transition: opacity 0.2s;
            pointer-events: none;
          ">
            <button onclick="event.stopPropagation(); window.showDeleteMessageMenu('${message.id}', event)" style="
              background: white;
              border: 1px solid #e8eaed;
              border-radius: 50%;
              width: 28px;
              height: 28px;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              font-size: 18px;
              padding: 0;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
              pointer-events: auto;
              transition: transform 0.2s;
              line-height: 1;
              color: #5f6368;
            " 
            onmouseover="this.style.transform='scale(1.15)'"
            onmouseout="this.style.transform='scale(1)'"
            title="More options">
              ⋮
            </button>
          </div>
        ` : ''}
      ` : ''}
    </div>
  `}
      
      <!-- Timestamp (hidden by default, show on last in group or when clicked) -->
      <div id="timestamp_${message.id}" style="
        display: ${isLastInGroup ? 'flex' : 'none'};
        align-items: center;
        gap: 2px;
        margin-top: 2px;
      ">
        <span style="
          font-size: 10px;
          color: #5f6368;
          padding: 2px 0 0 4px;
        ">${formatMessageTime(message.created_at)}</span>
        ${isOwnMessage ? `<span id="status_${message.id}">${isLastInGroup ? statusIcon : ''}</span>` : ''}
      </div>
    </div>
  `;
  
  return messageDiv;
};

// Track last clicked message
let lastClickedMessageId = null;

// Toggle timestamp visibility for a specific message
export const toggleMessageTimestamp = (messageId) => {
  const timestampEl = document.getElementById(`timestamp_${messageId}`);
  const statusEl = document.getElementById(`status_${messageId}`);
  const messageEl = document.querySelector(`[data-message-id="${messageId}"]`);
  
  if (!timestampEl) return;
  
  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  const userId = messageEl?.getAttribute('data-user-id');
  const isOwnMessage = userId === currentUserId;
  const messagesContainer = document.getElementById('messagesContainer');
  
  // Check if clicking the same message that's already shown
  if (lastClickedMessageId === messageId) {
    // Just hide it
    timestampEl.style.display = 'none';
    if (statusEl) {
      statusEl.innerHTML = '';
    }
    lastClickedMessageId = null;
    return;
  }
  
  // Hide ALL timestamps and status icons first
  const allTimestamps = messagesContainer.querySelectorAll('[id^="timestamp_"]');
  const allStatuses = messagesContainer.querySelectorAll('[id^="status_"]');
  
  allTimestamps.forEach(ts => ts.style.display = 'none');
  allStatuses.forEach(status => status.innerHTML = '');
  
  // Show the clicked message's timestamp
  timestampEl.style.display = 'flex';
  lastClickedMessageId = messageId;
  
  // If this is our message, show the status icon
  if (statusEl && isOwnMessage) {
    const messageData = window.currentChatMessages?.find(m => m.id === messageId);
    
    if (messageData) {
      const hasBeenRead = messageData.read_by && messageData.read_by.length > 0;
      
      if (hasBeenRead) {
        // Read - blue double checkmark
        statusEl.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="margin-left: 4px; flex-shrink: 0;">
            <path d="M1 12l5 5L17 6" stroke="#00BCD4" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M8 12l5 5L24 6" stroke="#00BCD4" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
      } else if (messageData.delivered_at) {
        // Delivered - grey double checkmark
        statusEl.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="margin-left: 4px; flex-shrink: 0;">
            <path d="M1 12l5 5L17 6" stroke="#9e9e9e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M8 12l5 5L24 6" stroke="#9e9e9e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
      } else if (messageData.sent_at) {
        // Sent - grey single checkmark
        statusEl.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="margin-left: 4px; flex-shrink: 0;">
            <path d="M4 12l6 6L20 6" stroke="#9e9e9e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
      }
    }
  }
};

// Helper function to open chat and handle red dot
export const openChatAndClearIndicator = async (eventId, eventTitle) => {
  
  // Immediately clear red dot (optimistic update)
  window.hasUnreadChats = false;
  window.dispatchEvent(new CustomEvent('unreadChatsChanged'));
  
  // Open the chat
  await window.openEventChat(eventId, eventTitle);
  
  // Mark messages as read after chat opens
  setTimeout(async () => {
    await window.markMessagesAsRead(eventId);
    
    // Check if there are STILL unread messages in other chats
    setTimeout(() => {
      const tempId = window.currentOpenChatId;
      window.currentOpenChatId = null; // Temporarily clear
      
      window.checkUnreadMessagesQuick().finally(() => {
        window.currentOpenChatId = tempId; // Restore
      });
    }, 300);
  }, 500);
};

// Load online statuses for all message users
export const loadChatUserStatuses = async (messages) => {
  const userIds = [...new Set(messages.map(m => m.user_id))];
  
  for (const userId of userIds) {
    const isOnline = await window.getUserOnlineStatus(userId);
    const statusColor = isOnline ? '#34a853' : '#9e9e9e';
    
    // Set CSS variable for this user
    document.documentElement.style.setProperty(`--status-color-${userId}`, statusColor);
  }
};

// Back to chats list from chat
export const backToChatsListFromChat = () => {
  // Close chat
  window.closeChatPanel();
  
  // Wait for animation
  setTimeout(() => {
    // Clear the flag
    window.openedFromChatsList = false;
    
    // Reopen chats list
    window.openChatsListPanel();
    
    // 🆕 Reload the chats list to show updated read status
    setTimeout(() => {
      window.loadChatsListDebounced();
    }, 100);
  }, 350);
};

// Open event details from chat
export const openEventDetailsFromChat = async (eventId) => {
    // 🆕 SET FLAG - we came from chat, not from chats list
  window.openedEventDetailsFromChat = true;
  window.openedFromChatsList = false; // Clear the chats list flag
  
  // Close chat
  window.closeChatPanel();
  
  // Wait for animation
  await new Promise(resolve => setTimeout(resolve, 350));
  
  // Fetch event data and open details
  try {
    const supabaseClient = window.supabase || supabase;
    const { data: eventData, error } = await supabaseClient
      .from('events')
      .select('*')
      .eq('event_id', eventId)
      .single();
    
    if (error) {
      window.showToast('Failed to load event details', 'error');
      return;
    }
    
    if (!eventData) {
      window.showToast('Event not found', 'error');
      return;
    }
    
    // Format the event data to match what showEventDetailsPanel expects
    const formattedEvent = {
      eventId: eventData.event_id,
      placeId: eventData.place_id,
      placeName: eventData.place_name,
      placeAddress: eventData.place_address,
      placePhotoUrl: eventData.place_photo_url,
      title: eventData.title,
      description: eventData.description,
      startDateTime: eventData.start_date_time,
      endDateTime: eventData.end_date_time,
      peopleLimit: eventData.people_limit,
      creatorId: eventData.creator_id,
      participants: eventData.participants,
      latitude: eventData.latitude,
      longitude: eventData.longitude,
      require_approve: eventData.require_appro,
      pending_requests: eventData.pending_requ || []
    };
    
    // Get photo URL
    const photoUrl = eventData.place_photo_url || 'https://via.placeholder.com/200';
    
    // Open event details
    await window.showEventDetailsPanel(formattedEvent, photoUrl);
    
  } catch (error) {
    window.showToast('Failed to load event details', 'error');
  }
};

// Open chat panel for an event
export const openEventChat = async (eventId, eventTitle) => {
    // 🆕 Track currently open chat
  window.currentOpenChatId = eventId;

  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  if (!currentUserId) {
    window.showToast('Please log in to access chat', 'error');
    return;
  }
  
  // Check if user is participant or organizer
  const supabaseClient = window.supabase || supabase;
const { data: eventData, error } = await supabaseClient
  .from('events')
  .select('creator_id, participants, title, chat_enabled, archived')
  .eq('event_id', eventId)
  .single();
  
  if (error || !eventData) {
    console.error('Error fetching event:', error);
    window.showToast('Could not load event data', 'error');
    return;
  }
  
const isOrganizer = eventData.creator_id === currentUserId;
  const isParticipant = eventData.participants?.includes(currentUserId);
  const isArchived = !!eventData.archived;
  window.currentChatIsArchived = isArchived;
  
  if (!isOrganizer && !isParticipant) {
    window.showToast('You must be a participant to access the chat', 'info');
    return;
  }

// 🆕 Check if user is muted
let isMuted = false;
if (!isOrganizer) {
  try {
    const { data: event } = await supabaseClient
      .from('events')
      .select('muted_users')
      .eq('event_id', eventId)
      .single();
    
    const mutedUsers = event?.muted_users || [];
    isMuted = mutedUsers.includes(currentUserId);
    
    console.log('💬 Chat opening - User muted status:', isMuted);
  } catch (error) {
    console.error('Error checking mute status:', error);
  }
}

  // Move event details panel behind chat (don't hide it)
const eventDetailsPanel = document.getElementById('eventDetailsPanel');
if (eventDetailsPanel) {
  eventDetailsPanel.style.zIndex = '999';
}
  
  // Remove existing chat panel if any
  const existingPanel = document.getElementById('chatPanel');
  if (existingPanel) {
    existingPanel.remove();
  }
  
  // Get current user info
  const currentUser = window.currentAppUser || window.currentUser;
  const userName = currentUser.profile?.name || 'User';
  const userAvatar = currentUser.profile?.profilePicture || null;
  
  // Create chat panel
  const chatPanel = document.createElement('div');
  chatPanel.id = 'chatPanel';
chatPanel.style.cssText = `
    position: fixed;
    top: 79px;
    right: -400px;
    width: 380px;
    max-width: calc(100vw - 4px);
    height: calc(100vh - 79px - var(--footer-height, 50px));
    background: white;
    border-bottom-left-radius: 20px;
    border-top-left-radius: 20px;
    box-shadow: -4px 0 12px rgba(0,0,0,0.15);
    border-left: 3px solid #4285f4;
    z-index: 10001;
    display: flex;
    flex-direction: column;
    transition: right 0.3s ease-out;
    overflow: hidden;
  `;
  
  chatPanel.innerHTML = `
<!-- Header -->
<div style="
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
">
  <!-- Back Button -->
  <button
    onclick="${window.openedFromChatsList && !window.openedEventDetailsFromChat ? 'window.backToChatsListFromChat()' : 'window.closeChatPanel()'}"
    style="
      background: none;
      border: none;
      cursor: pointer;
      padding: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: background 0.2s;
      flex-shrink: 0;
      margin-left: -8px;
    "
    onmouseover="this.style.background='#e8eaed'"
    onmouseout="this.style.background='none'"
    title="Back"
  >
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#5f6368" stroke-width="2">
      <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
  </button>
  
  <!-- Chat Icon -->
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#5f6368" stroke-width="2" style="flex-shrink: 0;">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
  </svg>
  
  <!-- Event Title -->
  <h3 style="
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #202124;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
  ">${escapeHtml(eventData.title)}</h3>
  
  ${window.openedFromChatsList && !window.openedEventDetailsFromChat ? `
<!-- Event Details Button (only when opened from chats list) -->
<button
  onclick="window.openEventDetailsFromChat('${eventId}')"
  style="
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background 0.2s;
    flex-shrink: 0;
  "
  onmouseover="this.style.background='#e8eaed'"
  onmouseout="this.style.background='none'"
  title="Event details"
>
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5f6368" stroke-width="2" stroke-linecap="round">
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
</button>
  ` : ''}
  
${isOrganizer && !isArchived ? `
    <!-- Settings Button (Organizer Only) -->
    <button
      onclick="window.toggleChatSettings('${eventId}')"
      style="
        background: none;
        border: none;
        cursor: pointer;
        padding: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: background 0.2s;
        flex-shrink: 0;
      "
      onmouseover="this.style.background='#e8eaed'"
      onmouseout="this.style.background='none'"
      title="Chat settings"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5f6368" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
        <circle cx="12" cy="12" r="3"></circle>
      </svg>
    </button>
  ` : ''}
</div>

<!-- Chat Settings Dropdown (Initially Hidden) -->
<div id="chatSettingsDropdown" style="
  display: none;
  background: white;
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
">
  <label style="
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    user-select: none;
  ">
<input
  type="checkbox"
  id="chatEnabledCheckbox"
  ${eventData.chat_enabled ? 'checked' : ''}
  onchange="window.toggleChatEnabled('${eventId}', this.checked)"
  style="
    width: 20px;
    height: 20px;
    cursor: pointer;
    accent-color: #00BCD4;
  "
/>
    <span style="
      font-size: 14px;
      font-weight: 500;
      color: #202124;
    ">Allow others to send messages</span>
  </label>
</div>
    
<!-- Messages Container -->
<div id="messagesContainer" 
  onscroll="window.handleChatScroll()"
  style="
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0 16px 16px 16px;
    background: #f8f9fa;
    display: flex;
    flex-direction: column;
    position: relative;
  ">
  
  ${isOrganizer && !eventData.chat_enabled ? `
    <!-- Chat Disabled Warning Banner (Sticky) -->
<div id="chatDisabledBanner" style="
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  background: #f1f3f4;
  border: 1px solid #dadce0;
  border-top: none;
  border-bottom-right-radius: 20px;
  border-bottom-left-radius: 20px;
  padding: 8px 12px;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
">
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5f6368" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
  </svg>
  <span style="font-size: 13px; font-weight: 500; color: #5f6368;">
    Messaging disabled
  </span>
</div>
  ` : ''}
  
<!-- Loading state -->
<div style="
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #5f6368;
">
  ${window.CustomLoadingSpinner({ size: 48, message: '' })}
</div>
</div>

    <!-- Scroll to Bottom Button -->
<button
  id="scrollToBottomBtn"
  onclick="window.scrollToBottom()"
  style="
    position: absolute;
    bottom: 100px;
    right: 45%;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: white;
    border: none;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    opacity: 0;
    pointer-events: none;
    transition: all 0.3s ease;
  "
  onmouseover="this.style.transform='scale(1.1)'; this.style.boxShadow='0 4px 12px rgba(0,0,0,0.3)'"
  onmouseout="this.style.transform='scale(1)'; this.style.boxShadow='0 2px 8px rgba(0,0,0,0.2)'"
  title="Scroll to bottom"
>
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#5f6368" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
</button>
    
<!-- Message Input -->
${eventData.archived || (!isOrganizer && !eventData.chat_enabled) || isMuted ? `
  <!-- Blocked Message Input -->
  <div id="messageInputContainer" style="
    padding: 16px;
    border-top: 1px solid #e0e0e0;
    flex-shrink: 0;
    background: white;
    position: relative;
  ">
    <div style="
      filter: blur(3px);
      pointer-events: none;
      opacity: 0.5;
    ">
      <div style="
        display: flex;
        gap: 8px;
        align-items: flex-end;
      ">
        <textarea
          placeholder="Type a message..."
          rows="1"
          disabled
          style="
            flex: 1;
            padding: 12px 16px;
            border: 1px solid #dadce0;
            border-radius: 24px;
            font-size: 14px;
            font-family: inherit;
            resize: none;
            background: #f5f5f5;
          "
        ></textarea>
        <button disabled style="
          width: 40px;
          height: 40px;
          border: none;
          border-radius: 50%;
          background: transparent;
          color: #5f6368;
        ">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
          </svg>
        </button>
        <button disabled style="
          width: 40px;
          height: 40px;
          border: none;
          border-radius: 50%;
          background: transparent;
          color: #5f6368;
        ">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
          </svg>
        </button>
      </div>
    </div>
<div style="
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
">
  <span style="
    font-size: 14px;
    font-weight: 600;
    color: #202124;
    text-shadow: 0 1px 3px rgba(255,255,255,0.8);
">${eventData.archived ? 'This event has been archived' : isMuted ? 'Organizer has muted you' : 'Messaging disabled'}</span>
</div>
  </div>
` : `
  <!-- Active Message Input -->
  <div id="messageInputContainer" style="
    padding: 16px;
    border-top: 1px solid #e0e0e0;
    flex-shrink: 0;
    background: white;
  ">
    <div style="
      display: flex;
      gap: 8px;
      align-items: flex-end;
    ">
      <textarea
        id="messageInput"
        placeholder="Type a message..."
        rows="1"
        maxlength="1000"
        style="
          flex: 1;
          padding: 12px 16px;
          border: 1px solid #dadce0;
          border-radius: 24px;
          font-size: 14px;
          font-family: inherit;
          resize: none;
          max-height: 120px;
          line-height: 1.4;
          outline: none;
        "
        oninput="this.style.height = 'auto'; this.style.height = Math.min(this.scrollHeight, 120) + 'px'"
        onkeydown="if(event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); window.sendMessage('${eventId}'); }"
      ></textarea>
      
      <!-- Emoji Picker Button -->
      <button
        id="emojiPickerButton"
        onclick="window.toggleEmojiPicker()"
        style="
          width: 40px;
          height: 40px;
          border: none;
          border-radius: 50%;
          background: transparent;
          color: #5f6368;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          flex-shrink: 0;
        "
        onmouseover="this.style.background='#f1f3f4'"
        onmouseout="this.style.background='transparent'"
        title="Add emoji"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
          <circle cx="9" cy="9" r="0.5" fill="currentColor"></circle>
          <circle cx="15" cy="9" r="0.5" fill="currentColor"></circle>
        </svg>
      </button>
      
      <button
        id="sendButton"
        onclick="window.sendMessage('${eventId}')"
        style="
          width: 40px;
          height: 40px;
          border: none;
          border-radius: 50%;
          background: transparent;
          color: #5f6368;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          flex-shrink: 0;
        "
        onmouseover="this.style.background='#f1f3f4'"
        onmouseout="this.style.background='transparent'"
        title="Send message"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
        </svg>
      </button>
    </div>
    
    <!-- Emoji Picker Popup -->
    <div id="emojiPicker" style="
      display: none;
      position: absolute;
      bottom: 80px;
      left: 16px;
      right: 16px;
      max-width: 350px;
      background: white;
      border-radius: 12px;
      box-shadow: 0 4px 16px rgba(0,0,0,0.2);
      padding: 12px;
      z-index: 10002;
      max-height: 300px;
      overflow-y: auto;
    ">
      <div style="
        display: grid;
        grid-template-columns: repeat(8, 1fr);
        gap: 8px;
      ">
        ${['😀','😃','😄','😁','😆','😅','🤣','😂','🙂','🙃','😉','😊','😇','🥰','😍','🤩','😘','😗','😚','😙','🥲','😋','😛','😜','🤪','😝','🤗','🤭','🤫','🤔','🤐','🤨','😐','😑','😶','😏','😒','🙄','😬','🤥','😌','😔','😪','🤤','😴','😷','🤒','🤕','🤢','🤮','🤧','🥵','🥶','🥴','😵','🤯','🤠','🥳','🥸','😎','🤓','🧐','😕','😟','🙁','☹️','😮','😯','😲','😳','🥺','😦','😧','😨','😰','😥','😢','😭','😱','😖','😣','😞','😓','😩','😫','🥱','😤','😡','😠','🤬','😈','👿','💀','☠️','💩','🤡','👹','👺','👻','👽','👾','🤖','😺','😸','😹','😻','😼','😽','🙀','😿','😾','👋','🤚','🖐️','✋','🖖','👌','🤌','🤏','✌️','🤞','🤟','🤘','🤙','👈','👉','👆','🖕','👇','☝️','👍','👎','✊','👊','🤛','🤜','👏','🙌','👐','🤲','🤝','🙏','✍️','💪','🦾','🦿','🦵','🦶','👂','🦻','👃','🧠','🫀','🫁','🦷','🦴','👀','👁️','👅','👄','💋','🩸','❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗','💖','💘','💝','💟','☮️','✝️','☪️','🕉️','☸️','✡️','🔯','🕎','☯️','☦️','🛐','⛎','♈','♉','♊','♋','♌','♍','♎','♏','♐','♑','♒','♓','🆔','⚛️','🉑','☢️','☣️','📴','📳','🈶','🈚','🈸','🈺','🈷️','✴️','🆚','💮','🉐','㊙️','㊗️','🈴','🈵','🈹','🈲','🅰️','🅱️','🆎','🆑','🅾️','🆘','❌','⭕','🛑','⛔','📛','🚫','💯','💢','♨️','🚷','🚯','🚳','🚱','🔞','📵','🚭','❗','❕','❓','❔','‼️','⁉️','🔅','🔆','〽️','⚠️','🚸','🔱','⚜️','🔰','♻️','✅','🈯','💹','❇️','✳️','❎','🌐','💠','Ⓜ️','🌀','💤','🏧','🚾','♿','🅿️','🈳','🈂️','🛂','🛃','🛄','🛅','🚹','🚺','🚼','⚧️','🚻','🚮','🎦','📶','🈁','🔣','ℹ️','🔤','🔡','🔠','🆖','🆗','🆙','🆒','🆕','🆓','0️⃣','1️⃣','2️⃣','3️⃣','4️⃣','5️⃣','6️⃣','7️⃣','8️⃣','9️⃣','🔟','🔢','#️⃣','*️⃣','⏏️','▶️','⏸️','⏯️','⏹️','⏺️','⏭️','⏮️','⏩','⏪','⏫','⏬','◀️','🔼','🔽','➡️','⬅️','⬆️','⬇️','↗️','↘️','↙️','↖️','↕️','↔️','↪️','↩️','⤴️','⤵️','🔀','🔁','🔂','🔄','🔃','🎵','🎶','➕','➖','➗','✖️','♾️','💲','💱','™️','©️','®️','〰️','➰','➿','🔚','🔙','🔛','🔝','🔜','✔️','☑️','🔘','🔴','🟠','🟡','🟢','🔵','🟣','⚫','⚪','🟤','🔺','🔻','🔸','🔹','🔶','🔷','🔳','🔲','▪️','▫️','◾','◽','◼️','◻️','🟥','🟧','🟨','🟩','🟦','🟪','⬛','⬜','🟫','🔈','🔇','🔉','🔊','🔔','🔕','📣','📢','👁️‍🗨️','💬','💭','🗯️','♠️','♣️','♥️','♦️','🃏','🎴','🀄','🕐','🕑','🕒','🕓','🕔','🕕','🕖','🕗','🕘','🕙','🕚','🕛','🕜','🕝','🕞','🕟','🕠','🕡','🕢','🕣','🕤','🕥','🕦','🕧'].map(emoji => 
          `<button onclick="window.insertEmoji('${emoji}')" style="
            width: 32px;
            height: 32px;
            border: none;
            background: transparent;
            font-size: 24px;
            cursor: pointer;
            border-radius: 4px;
            transition: background 0.2s;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
          " onmouseover="this.style.background='#f1f3f4'" onmouseout="this.style.background='transparent'">${emoji}</button>`
        ).join('')}
      </div>
    </div>
  </div>
`}
  `;
  
  document.body.appendChild(chatPanel);
  
  // Slide in animation
  setTimeout(() => {
    chatPanel.style.right = '0';
  }, 10);
  
// Load existing messages
await window.loadMessages(eventId, eventData.chat_enabled, isOrganizer);
  
  // Set up real-time subscription for new messages
  window.setupChatRealtimeSubscription(eventId);

  // Mark messages as delivered
await window.markMessagesAsDelivered(eventId);

// 🆕 Listen for mute status changes (real-time)
if (!isOrganizer) {
  window.muteStatusSubscription = supabaseClient
    .channel(`mute-status-${eventId}-${currentUserId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'events',
        filter: `event_id=eq.${eventId}`
      },
      (payload) => {
        console.log('🔔 Received UPDATE event from Supabase');
        
        const mutedUsers = payload.new.muted_users || [];
        const isNowMuted = mutedUsers.includes(currentUserId);
        
        const messageInputContainer = document.getElementById('messageInputContainer');
        
        if (!messageInputContainer) {
          return;
        }
        
        if (isNowMuted) {
          const blockedHTML = `
            <div id="messageInputContainer" style="padding: 16px; border-top: 1px solid #e0e0e0; flex-shrink: 0; background: white; position: relative;">
              <div style="filter: blur(3px); pointer-events: none; opacity: 0.5;">
                <div style="display: flex; gap: 8px; align-items: flex-end;">
                  <textarea placeholder="Type a message..." rows="1" disabled style="flex: 1; padding: 12px 16px; border: 1px solid #dadce0; border-radius: 24px; font-size: 14px; resize: none; background: #f5f5f5; font-family: inherit; max-height: 120px; line-height: 1.4; outline: none;"></textarea>
                  <button disabled style="width: 40px; height: 40px; border: none; border-radius: 50%; background: transparent; color: #5f6368; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                    </svg>
                  </button>
                  <button disabled style="width: 40px; height: 40px; border: none; border-radius: 50%; background: transparent; color: #5f6368; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); text-align: center; pointer-events: none;">
                <span style="font-size: 14px; font-weight: 600; color: #202124; text-shadow: 0 1px 3px rgba(255,255,255,0.8);">Organizer has muted you</span>
              </div>
            </div>
          `;
          
          messageInputContainer.outerHTML = blockedHTML;
        } else {
          const activeHTML = `
            <div id="messageInputContainer" style="padding: 16px; border-top: 1px solid #e0e0e0; flex-shrink: 0; background: white;">
              <div style="display: flex; gap: 8px; align-items: flex-end;">
                <textarea id="messageInput" placeholder="Type a message..." rows="1" maxlength="1000" style="flex: 1; padding: 12px 16px; border: 1px solid #dadce0; border-radius: 24px; font-size: 14px; font-family: inherit; resize: none; max-height: 120px; line-height: 1.4; outline: none;" oninput="this.style.height = 'auto'; this.style.height = Math.min(this.scrollHeight, 120) + 'px'" onkeydown="if(event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); window.sendMessage('${eventId}'); }"></textarea>
                <button id="emojiPickerButton" onclick="window.toggleEmojiPicker()" style="width: 40px; height: 40px; border: none; border-radius: 50%; background: transparent; color: #5f6368; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; flex-shrink: 0;" onmouseover="this.style.background='#f1f3f4'" onmouseout="this.style.background='transparent'" title="Add emoji">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                    <circle cx="9" cy="9" r="0.5" fill="currentColor"></circle>
                    <circle cx="15" cy="9" r="0.5" fill="currentColor"></circle>
                  </svg>
                </button>
                <button id="sendButton" onclick="window.sendMessage('${eventId}')" style="width: 40px; height: 40px; border: none; border-radius: 50%; background: transparent; color: #5f6368; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; flex-shrink: 0;" onmouseover="this.style.background='#f1f3f4'" onmouseout="this.style.background='transparent'" title="Send message">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                  </svg>
                </button>
              </div>
              <div id="emojiPicker" style="display: none; position: absolute; bottom: 80px; left: 16px; right: 16px; max-width: 350px; background: white; border-radius: 12px; box-shadow: 0 4px 16px rgba(0,0,0,0.2); padding: 12px; z-index: 10002; max-height: 300px; overflow-y: auto;">
                <div style="display: grid; grid-template-columns: repeat(8, 1fr); gap: 8px;">
                  ${['😀','😃','😄','😁','😆','😅','🤣','😂','🙂','🙃','😉','😊','😇','🥰','😍','🤩','😘','😗','😚','😙','🥲','😋','😛','😜','🤪','😝','🤗','🤭','🤫','🤔','🤐','🤨','😐','😑','😶','😏','😒','🙄','😬','😮','😯','😲','😳','🥺','😦','😧','😨','😰','😥','😢','😭','😱','😖','😣','😞','😓','😩','😫','🥱','😤','😡','😠','🤬','😈','👿','💀','☠️','💩','🤡','👹','👺','👻','👽','👾','🤖','😺','😸','😹','😻','😼','😽','🙀','😿','😾','👋','🤚','🖐️','✋','🖖','👌','🤌','🤏','✌️','🤞','🤟','🤘','🤙','👈','👉','👆','🖕','👇','☝️','👍','👎','✊','👊','🤛','🤜','👏','🙌','👐','🤲','🤝','🙏','✍️','💪','❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗','💖','💘','💝','💟'].map(emoji => 
                    `<button onclick="window.insertEmoji('${emoji}')" style="width: 32px; height: 32px; border: none; background: transparent; font-size: 24px; cursor: pointer; border-radius: 4px; transition: background 0.2s; padding: 0; display: flex; align-items: center; justify-content: center;" onmouseover="this.style.background='#f1f3f4'" onmouseout="this.style.background='transparent'">${emoji}</button>`
                  ).join('')}
                </div>
              </div>
            </div>
          `;
          
          messageInputContainer.outerHTML = activeHTML;
          
          // Focus on the new input
          setTimeout(() => {
            const newInput = document.getElementById('messageInput');
            if (newInput) {
              newInput.focus();
            }
          }, 100);
        }
      }
    )
    .subscribe(() => {});
}
  
  // Focus on input (desktop only — on mobile auto-focus opens keyboard unexpectedly)
  if (window.innerWidth > 800) {
    setTimeout(() => {
      document.getElementById('messageInput')?.focus();
    }, 350);
  }
  
  // Store event info for later use
  chatPanel.dataset.eventId = eventId;
  chatPanel.dataset.userName = userName;
  chatPanel.dataset.userAvatar = userAvatar || '';
};

export const closeChatPanel = () => {
  const chatPanel = document.getElementById('chatPanel');
  if (chatPanel) {
    chatPanel.style.right = '-400px';
    
    // 🆕 Clear currently open chat
    window.currentOpenChatId = null;
    
    // Clean up subscription
    if (window.chatSubscription) {
      const supabaseClient = window.supabase || supabase;
      supabaseClient.removeChannel(window.chatSubscription);
      window.chatSubscription = null;
    }
    
    // Clean up mute status subscription
    if (window.muteStatusSubscription) {
      const supabaseClient = window.supabase || supabase;
      supabaseClient.removeChannel(window.muteStatusSubscription);
      window.muteStatusSubscription = null;
    }
    
    // Clean up status polling interval
    if (window.chatStatusInterval) {
      clearInterval(window.chatStatusInterval);
      window.chatStatusInterval = null;
    }
    
    
    // Restore event details panel z-index
    const eventDetailsPanel = document.getElementById('eventDetailsPanel');
    if (eventDetailsPanel) {
      eventDetailsPanel.style.zIndex = '1000';
    }
    
    setTimeout(() => {
      chatPanel.remove();
    }, 300);
  }
};

export const cleanupAllSubscriptions = () => {
  const supabaseClient = window.supabase || supabase;
  
  // Clean up global message listener
  if (window.globalMessageListener) {
    supabaseClient.removeChannel(window.globalMessageListener);
    window.globalMessageListener = null;
  }
  
  // Clean up chat subscription
  if (window.chatSubscription) {
    supabaseClient.removeChannel(window.chatSubscription);
    window.chatSubscription = null;
  }
  
  // Clean up mute status subscription
  if (window.muteStatusSubscription) {
    supabaseClient.removeChannel(window.muteStatusSubscription);
    window.muteStatusSubscription = null;
  }
  
  // Clean up intervals
  if (window.chatStatusInterval) {
    clearInterval(window.chatStatusInterval);
    window.chatStatusInterval = null;
  }
};

// Show/hide scroll to bottom button based on scroll position
export const handleChatScroll = () => {
  const messagesContainer = document.getElementById('messagesContainer');
  const scrollButton = document.getElementById('scrollToBottomBtn');
  
  if (!messagesContainer || !scrollButton) return;
  
  // Check if user has scrolled up (not at bottom)
  const isAtBottom = messagesContainer.scrollHeight - messagesContainer.scrollTop - messagesContainer.clientHeight < 100;
  
  if (isAtBottom) {
    scrollButton.style.opacity = '0';
    scrollButton.style.pointerEvents = 'none';
  } else {
    scrollButton.style.opacity = '1';
    scrollButton.style.pointerEvents = 'auto';
  }
};

// Scroll to bottom of chat
export const scrollToBottom = () => {
  const messagesContainer = document.getElementById('messagesContainer');
  if (messagesContainer) {
    messagesContainer.scrollTo({
      top: messagesContainer.scrollHeight,
      behavior: 'smooth'
    });
  }
};

// Toggle chat settings dropdown
export const toggleChatSettings = (eventId) => {
  const dropdown = document.getElementById('chatSettingsDropdown');
  if (dropdown) {
    dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
  }
};

export const toggleChatEnabled = async (eventId, enabled) => {

  try {
    const supabaseClient = window.supabase || supabase;
    
    // Update in database
    const { error } = await supabaseClient
      .from('events')
      .update({ chat_enabled: enabled })
      .eq('event_id', eventId);
    
    if (error) {
      // Revert checkbox
      const checkbox = document.getElementById('chatEnabledCheckbox');
      if (checkbox) checkbox.checked = !enabled;
      return;
    }
    
    // 🆕 Show/hide banner for organizer
    const messagesContainer = document.getElementById('messagesContainer');
    let banner = document.getElementById('chatDisabledBanner');
    
    if (!enabled && messagesContainer && !banner) {
      // Create banner
      const bannerHTML = `
<div id="chatDisabledBanner" style="
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  background: #f1f3f4;
  border: 1px solid #dadce0;
  border-top: none;
  border-bottom-right-radius: 20px;
  border-bottom-left-radius: 20px;
  padding: 8px 12px;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
">
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5f6368" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
  </svg>
  <span style="font-size: 13px; font-weight: 500; color: #5f6368;">
    Messaging disabled
  </span>
</div>
      `;
      
      messagesContainer.insertAdjacentHTML('afterbegin', bannerHTML);
    } else if (enabled && banner) {
      // Remove banner
      banner.remove();
    }
    
  } catch (error) {
    console.error('❌ Error in toggleChatEnabled:', error);
  }
};

export const loadMessages = async (eventId, chatEnabled = true, isOrganizer = false) => {
  const messagesContainer = document.getElementById('messagesContainer');
  if (!messagesContainer) {
    return;
  }
  
  try {
    const supabaseClient = window.supabase || supabase;
    // 🆕 Save the banner if it exists OR create banner HTML if needed
    const existingBanner = document.getElementById('chatDisabledBanner');
    let bannerHTML = existingBanner ? existingBanner.outerHTML : null;
    
    // 🆕 If banner doesn't exist but chat is disabled for organizer, create it
    if (!bannerHTML && isOrganizer && !chatEnabled) {
      bannerHTML = `
<div id="chatDisabledBanner" style="
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  background: #f1f3f4;
  border: 1px solid #dadce0;
  border-top: none;
  border-bottom-right-radius: 20px;
  border-bottom-left-radius: 20px;
  padding: 8px 12px;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
">
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5f6368" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
  </svg>
  <span style="font-size: 13px; font-weight: 500; color: #5f6368;">
    Messaging disabled
  </span>
</div>
      `;
    }

    const { data: messages, error } = await supabaseClient
      .from('messages')
      .select('*')
      .eq('event_id', eventId)
      .order('created_at', { ascending: true });
    
    if (error) {
      throw error;
    }

    // Store messages globally for click handler access
    window.currentChatMessages = messages;
    window.currentChatEventId = eventId;

    // Clear container
    messagesContainer.innerHTML = '';

    // 🆕 Restore/add banner first if it should exist
    if (bannerHTML) {
      messagesContainer.insertAdjacentHTML('afterbegin', bannerHTML);
    }

if (!messages || messages.length === 0) {
  // Keep the banner, just add empty state after it
  messagesContainer.insertAdjacentHTML('beforeend', `
    <div style="
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1;
      color: #5f6368;
      font-size: 14px;
    ">
      <p>No messages yet. Start the conversation!</p>
    </div>
  `);
  return;
}

    // Group messages by date AND by sender
    let lastDate = null;
    const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
    
// Process each message and determine its grouping
messages.forEach((message, index) => {
  const prevMsg = messages[index - 1];
  const nextMsg = messages[index + 1];
  
  // Check if we need a date divider
  const messageDate = new Date(message.created_at);
  
  // Validate date
  if (isNaN(messageDate.getTime())) {
    return; // Skip this message
  }
  
  const messageDateString = messageDate.toLocaleDateString();
  
  // Check if date changed (this will break grouping)
  const dateChanged = messageDateString !== lastDate;
  
  if (dateChanged) {
    const dateDivider = document.createElement('div');
    dateDivider.style.cssText = 'text-align: center; margin: 8px 0;';
    dateDivider.innerHTML = `
      <span style="
        font-size: 11px;
        color: #5f6368;
        background: #e8eaed;
        padding: 4px 12px;
        border-radius: 12px;
      ">${formatDateDivider(message.created_at)}</span>
    `;
    messagesContainer.appendChild(dateDivider);
    lastDate = messageDateString;
  }
  
  // Determine if this is first/last in group
  const isOwnMessage = message.user_id === currentUserId;
  
  // Check if previous message was on a different date
  const prevMessageDate = prevMsg ? new Date(prevMsg.created_at).toLocaleDateString() : null;
  const prevDateDifferent = prevMessageDate !== messageDateString;
  
  // Check if next message is on a different date
  const nextMessageDate = nextMsg ? new Date(nextMsg.created_at).toLocaleDateString() : null;
  const nextDateDifferent = nextMessageDate !== messageDateString;
  
  // First in group: no previous message OR different user OR different date
  const isFirstInGroup = !prevMsg || 
                         prevMsg.user_id !== message.user_id ||
                         prevDateDifferent;

  // Last in group: no next message OR different user OR different date
  const isLastInGroup = !nextMsg || 
                        nextMsg.user_id !== message.user_id ||
                        nextDateDifferent;
      
// Add message with grouping info
messagesContainer.appendChild(
  createMessageElement(message, isOwnMessage, isLastInGroup, isFirstInGroup, false)
);

// Load reactions for this message if they exist
if (message.reactions && Object.keys(message.reactions).length > 0) {
  // Use setTimeout to ensure DOM is ready
  setTimeout(() => {
    window.updateReactionsUI(message.id, message.reactions);
  }, 0);
}
    });
    
// Scroll to absolute bottom - use multiple strategies
const scrollToBottom = () => {
  // Strategy 1: Set scrollTop to maximum possible value
  messagesContainer.scrollTop = messagesContainer.scrollHeight;
  
  // Strategy 2: Scroll the last child into view at the nearest edge
  const lastChild = messagesContainer.lastElementChild;
  if (lastChild) {
    lastChild.scrollIntoView({ behavior: 'instant', block: 'nearest' });
  }
  
  // Strategy 3: Force scrollTop again after layout
  requestAnimationFrame(() => {
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  });
};

// Execute scroll immediately
scrollToBottom();

// And once more after reactions load
setTimeout(scrollToBottom, 100);

// Load online statuses
await window.loadChatUserStatuses(messages);

// Mark messages as delivered (when they load)
await window.markMessagesAsDelivered(eventId);

// Mark messages as read (when user views them)
await window.markMessagesAsRead(eventId);

// Wait for all UPDATE events to finish, then clean up checkmarks
setTimeout(() => {
  const messagesContainer = document.getElementById('messagesContainer');
  if (!messagesContainer) return;
  
  // Set a flag to prevent UPDATE handler from adding checkmarks
  window.checkmarkCleanupComplete = true;
  
  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  const allMessageElements = messagesContainer.querySelectorAll('[data-message-id]');
  
  // Find the absolute last own message using data-user-id attribute
  let lastOwnMessageId = null;
  
  Array.from(allMessageElements).reverse().forEach(msgEl => {
    if (lastOwnMessageId) return; // Already found it
    
    const userId = msgEl.getAttribute('data-user-id');
    if (userId === currentUserId) {
      lastOwnMessageId = msgEl.getAttribute('data-message-id');
    }
  });
  
  // AGGRESSIVELY clear ALL status icons first
  document.querySelectorAll('[id^="status_"]').forEach(el => {
    el.innerHTML = '';
  });
  
  // Then add checkmark ONLY to the last message
  if (lastOwnMessageId) {
    const lastStatusEl = document.getElementById(`status_${lastOwnMessageId}`);
    if (lastStatusEl) {
      const lastMsg = messages.find(m => m.id === lastOwnMessageId);
      if (lastMsg) {
        const hasBeenRead = lastMsg.read_by && lastMsg.read_by.length > 0;
        
        if (hasBeenRead) {
          lastStatusEl.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="margin-left: 4px; flex-shrink: 0;">
              <path d="M1 12l5 5L17 6" stroke="#00BCD4" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M8 12l5 5L24 6" stroke="#00BCD4" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          `;
        } else if (lastMsg.delivered_at) {
          lastStatusEl.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="margin-left: 4px; flex-shrink: 0;">
              <path d="M1 12l5 5L17 6" stroke="#9e9e9e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M8 12l5 5L24 6" stroke="#9e9e9e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          `;
        } else if (lastMsg.sent_at) {
          lastStatusEl.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="margin-left: 4px; flex-shrink: 0;">
              <path d="M4 12l6 6L20 6" stroke="#9e9e9e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          `;
        }
      }
    }
  }
}, 800);
    
  } catch (error) {
    messagesContainer.innerHTML = `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: #ea4335;
        font-size: 14px;
      ">
        <p>Failed to load messages</p>
      </div>
    `;
  }
};

export const sendMessage = async (eventId) => {
  const messageInput = document.getElementById('messageInput');
  if (!messageInput) return;
  
  const messageText = messageInput.value.trim();
  if (!messageText) return;
  
  const supabaseClient = window.supabase || supabase;
  const currentUser = window.currentAppUser || window.currentUser;

// 🆕 Check if user is muted
try {
  const { data: event } = await supabaseClient
    .from('events')
    .select('muted_users')
    .eq('event_id', eventId)
    .single();
  
  const mutedUsers = event?.muted_users || [];
  if (mutedUsers.includes(currentUser.uid)) {
    // Don't show toast - the UI already shows they're muted
    messageInput.value = ''; // Clear the input
    return;
  }
} catch (error) {
  console.error('Error checking mute status:', error);
}
  
  // Create a temporary message ID
  const tempMessageId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  // Create optimistic message object
  const optimisticMessage = {
    id: tempMessageId,
    event_id: eventId,
    user_id: currentUser.uid,
    user_name: currentUser.profile?.name || 'User',
    user_avatar: currentUser.profile?.profilePicture || null,
    message: messageText,
    created_at: new Date().toISOString(),
    sent_at: null, // Not sent yet
    delivered_at: null,
    is_deleted: false,
    read_by: []
  };
  
  // Clear input immediately for better UX
  messageInput.value = '';
  messageInput.style.height = 'auto';
  
  // Add message to UI immediately (optimistic update)
  const messagesContainer = document.getElementById('messagesContainer');
  if (messagesContainer) {
    // Remove empty state if present
    const emptyState = messagesContainer.querySelector('p');
    if (emptyState && emptyState.textContent.includes('No messages yet')) {
      messagesContainer.innerHTML = '';
    }
    
    // Check for date divider
    const messageDate = new Date(optimisticMessage.created_at);
    const existingDividers = Array.from(messagesContainer.querySelectorAll('div[style*="text-align: center"] span'));
    const hasDateDivider = existingDividers.some(divider => 
      divider.textContent === formatDateDivider(optimisticMessage.created_at)
    );
    
    if (!hasDateDivider) {
      const dateDivider = document.createElement('div');
      dateDivider.style.cssText = 'text-align: center; margin: 8px 0;';
      dateDivider.innerHTML = `
        <span style="
          font-size: 11px;
          color: #5f6368;
          background: #e8eaed;
          padding: 4px 12px;
          border-radius: 12px;
        ">${formatDateDivider(optimisticMessage.created_at)}</span>
      `;
      messagesContainer.appendChild(dateDivider);
    }
    
// Check grouping - determine if this message should be grouped with previous
    const allMessageContainers = Array.from(messagesContainer.querySelectorAll('div[style*="flex-direction"]'));
    let isFirstInGroup = true;
    let isLastInGroup = true; // New messages are always last
    
    if (allMessageContainers.length > 0) {
      const lastContainer = allMessageContainers[allMessageContainers.length - 1];
      const lastMessageEl = lastContainer.querySelector('[data-message-id]');
      
      if (lastMessageEl) {
        const lastMsgId = lastMessageEl.getAttribute('data-message-id');
        const lastIsOwn = lastContainer.style.flexDirection.includes('row-reverse');
        
        // If last message is also from us, group them
        if (lastIsOwn) {
          isFirstInGroup = false;
          
          // Update previous message to not be last in group
          lastContainer.style.marginBottom = '2px';
          
          // Hide timestamp on previous message
          const prevTimestamp = document.getElementById(`timestamp_${lastMsgId}`);
          if (prevTimestamp) {
            prevTimestamp.style.display = 'none';
          }
          
          // Clear status on previous message
          const prevStatus = document.getElementById(`status_${lastMsgId}`);
          if (prevStatus) {
            prevStatus.innerHTML = '';
          }
        }
      }
    }
    
    // Add optimistic message to UI
    const messageEl = createMessageElement(optimisticMessage, true, isLastInGroup, isFirstInGroup, false);
    messagesContainer.appendChild(messageEl);
    
    // Ensure the new message container has correct spacing
    const newContainer = messageEl;
    if (newContainer) {
      newContainer.style.marginBottom = '12px'; // Last message spacing
    }
    
    // 🆕 Clear checkmarks and timestamps from ALL previous messages (not just grouped ones)
    const allMessages = messagesContainer.querySelectorAll('[data-message-id]');
    allMessages.forEach(msgEl => {
      const msgId = msgEl.getAttribute('data-message-id');
      
      // Skip the message we just added
      if (msgId === tempMessageId) return;
      
      // Check if it's an own message (row-reverse)
      const msgContainer = msgEl.closest('div[style*="flex-direction"]');
      if (msgContainer && msgContainer.style.flexDirection.includes('row-reverse')) {
        // Clear timestamp and status
        const timestampEl = document.getElementById(`timestamp_${msgId}`);
        if (timestampEl) {
          timestampEl.style.display = 'none';
        }
        
        const statusEl = document.getElementById(`status_${msgId}`);
        if (statusEl) {
          // Only clear if not an error icon
          if (!statusEl.innerHTML.includes('stroke="#ea4335"')) {
            statusEl.innerHTML = '';
          }
        }
      }
    });
    
    // Scroll to bottom
    messagesContainer.scrollTo({
      top: messagesContainer.scrollHeight,
      behavior: 'smooth'
    });
  }
  
  // Try to send to server
  try {
    const { data, error } = await supabaseClient
      .from('messages')
      .insert({
        event_id: eventId,
        user_id: currentUser.uid,
        user_name: currentUser.profile?.name || 'User',
        user_avatar: currentUser.profile?.profilePicture || null,
        message: messageText,
        created_at: optimisticMessage.created_at,
        sent_at: new Date().toISOString(),
        delivered_at: new Date().toISOString(),
        is_deleted: false
      })
      .select()
      .single();
    
    if (error) throw error;
  } catch (error) {
    // Update UI to show failed state
    const tempMsgElement = document.querySelector(`[data-message-id="${tempMessageId}"]`);
    if (tempMsgElement) {
      const statusElement = document.getElementById(`status_${tempMessageId}`);
      if (statusElement) {
        statusElement.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="margin-left: 4px; flex-shrink: 0;">
            <circle cx="12" cy="12" r="10" stroke="#ea4335" stroke-width="2"/>
            <path d="M12 8v4M12 16h.01" stroke="#ea4335" stroke-width="2" stroke-linecap="round"/>
          </svg>
        `;
      }
    }
    
    // Show error message
    window.showToast?.('Message failed to send', 'error') || window.showToast('Failed to send message', 'error');
  }
};

// Mark message as delivered
export const markMessageAsDelivered = async (messageId) => {
  try {
    await supabase
      .from('messages')
      .update({ delivered_at: new Date().toISOString() })
      .eq('id', messageId);
    
    // Update UI
    const statusEl = document.getElementById(`status_${messageId}`);
    if (statusEl) {
      statusEl.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="margin-left: 4px; flex-shrink: 0;">
          <path d="M1 12l5 5L17 6" stroke="#00BCD4" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M8 12l5 5L24 6" stroke="#00BCD4" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
    }
  } catch (error) {
    console.error('Error marking message as delivered:', error);
  }
};

// Mark messages as delivered when loaded
export const markMessagesAsDelivered = async (eventId) => {
  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  
  try {
    const supabaseClient = window.supabase || supabase;
    
    // Get all undelivered messages not from current user
    const { data: messages, error } = await supabaseClient
      .from('messages')
      .select('id')
      .eq('event_id', eventId)
      .neq('user_id', currentUserId)
      .is('delivered_at', null);
    
    if (error) {
      return;
    }
    
    if (messages && messages.length > 0) {
      // Mark them as delivered
      const messageIds = messages.map(m => m.id);
      
      const {} = await supabaseClient
        .from('messages')
        .update({ delivered_at: new Date().toISOString() })
        .in('id', messageIds);
      
    }
  } catch (error) {
    console.error('Error marking messages as delivered:', error);
  }
};

export const markMessagesAsRead = async (eventId) => {
  window.isMarkingAsRead = true;
  
  try {
    const supabaseClient = window.supabase || supabase;
    const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
    
    if (!currentUserId) {
      window.isMarkingAsRead = false;
      return;
    }
    
    // Get all unread messages in this event
    const { data: unreadMessages } = await supabaseClient
      .from('messages')
      .select('id, read_by')
      .eq('event_id', eventId)
      .neq('user_id', currentUserId);
    
    if (!unreadMessages || unreadMessages.length === 0) {
      window.isMarkingAsRead = false;
      return;
    }
    
    // Filter messages that haven't been read by current user
    const messagesToUpdate = unreadMessages.filter(msg => {
      const readBy = msg.read_by || [];
      return !readBy.includes(currentUserId);
    });
    
    if (messagesToUpdate.length === 0) {
      window.isMarkingAsRead = false;
      return;
    }
    
    // Update each message
    for (const message of messagesToUpdate) {
      const readBy = message.read_by || [];
      const updatedReadBy = [...readBy, currentUserId];
      
      await supabaseClient
        .from('messages')
        .update({ read_by: updatedReadBy })
        .eq('id', message.id);
    }
    
    // ONLY update red dot if this is NOT the currently open chat
    setTimeout(() => {
      if (eventId !== window.currentOpenChatId) {
        window.checkUnreadMessagesQuick();
      }
    }, 100);
    
  } catch (error) {
    console.error('❌ Error marking messages as read:', error);
  } finally {
    setTimeout(() => {
      window.isMarkingAsRead = false;
    }, 500);
  }
};

// Mark a single message as read immediately
export const markSingleMessageAsRead = async (messageId, currentReadBy) => {
  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  
  // Check if already read by this user
  if (currentReadBy.includes(currentUserId)) {
    return;
  }
  
  try {
    const supabaseClient = window.supabase || supabase;
    const newReadBy = [...currentReadBy, currentUserId];

    await supabaseClient
      .from('messages')
      .update({ read_by: newReadBy })
      .eq('id', messageId);
    
  } catch (error) {
    console.error('Error marking message as read:', error);
  }
};

// Update a specific chat row with new message
export const updateChatRow = async (message) => {
  try {
    const supabaseClient = window.supabase || supabase;
    const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
    
    if (!currentUserId) return;
    // Find the chat row for this event
    const container = document.getElementById('chatsListContainer');
    if (!container) {
      return;
    }
    
    // Get the event data
    const { data: event } = await supabaseClient
      .from('events')
      .select('event_id, title, creator_id')
      .eq('event_id', message.event_id)
      .single();
    
    if (!event) {
      return;
    }
    
    // Get sender name
    let senderName = 'Someone';
    if (message.user_id === currentUserId) {
      senderName = 'You';
    } else {
      const { data: sender } = await supabaseClient
        .from('users')
        .select('name')
        .eq('id', message.user_id)
        .single();
      senderName = sender?.name || 'Someone';
    }
    
    const isUnread = message.user_id !== currentUserId;
    
    // Format message preview
    let displayMessage = `${senderName}: ${message.message}`;
    if (displayMessage.length > 40) {
      displayMessage = displayMessage.substring(0, 37) + '...';
    }
    
    const timeAgo = window.formatTimeAgo(message.created_at);
    
    // Find the existing chat row by looking for onclick with this event_id
    const allRows = container.querySelectorAll('[onclick*="openChatFromList"]');
    let existingRow = null;
    
    for (const row of allRows) {
      if (row.getAttribute('onclick').includes(event.event_id)) {
        existingRow = row;
        break;
      }
    }
    
    if (existingRow) {
      // Find elements to update
      const titleElement = existingRow.querySelector('h4');
      const messageElement = existingRow.querySelector('p[style*="font-size: 13px"]');
      const timeElements = existingRow.querySelectorAll('span[style*="font-size: 11px"]');
      const timeElement = timeElements[timeElements.length - 1]; // Last one is the time
      const iconContainer = existingRow.querySelector('[style*="position: relative"]');
      
      // Update title to bold if unread
      if (isUnread && titleElement) {
        titleElement.style.fontWeight = '700';
      }
      
      // Update message text and make bold if unread
      if (messageElement) {
        messageElement.textContent = displayMessage;
        if (isUnread) {
          messageElement.style.fontWeight = '600';
          messageElement.style.color = '#202124';
        }
      }
      
      // Update time and make bold if unread
      if (timeElement) {
        timeElement.textContent = timeAgo;
        if (isUnread) {
          timeElement.style.fontWeight = '600';
          timeElement.style.color = '#202124';
        }
      }
      
      // Add red dot on icon if unread
      if (isUnread && iconContainer) {
        const existingDot = iconContainer.querySelector('span[style*="position: absolute"]');
        if (!existingDot) {
          const redDot = document.createElement('span');
          redDot.style.cssText = `
            position: absolute;
            top: 0;
            right: 0;
            width: 10px;
            height: 10px;
            background: #ea4335;
            border-radius: 50%;
            border: 2px solid white;
          `;
          iconContainer.appendChild(redDot);
        }
      }
      
      // Update background if unread
      if (isUnread) {
        existingRow.style.background = '#f8f9fa';
        existingRow.setAttribute('onmouseout', "this.style.background='#f8f9fa'");
      }
      
      // Move row to top
      container.insertBefore(existingRow, container.firstChild);
    } else {
      window.loadChatsList();
    }
    
    // Update red dot indicator
    window.hasUnreadChats = true;
    window.dispatchEvent(new CustomEvent('unreadChatsChanged'));
    
  } catch (error) {
    console.error('❌ Error updating chat row:', error);
  }
};

// Set up real-time subscription for chat
export const setupChatRealtimeSubscription = (eventId) => {
  const supabaseClient = window.supabase || supabase;
  
  // Clean up existing subscription if any
  if (window.chatSubscription) {
    supabaseClient.removeChannel(window.chatSubscription);
  }
  
  // Clean up existing status interval
  if (window.chatStatusInterval) {
    clearInterval(window.chatStatusInterval);
  }

  // Function to update all chat user statuses
  const updateAllChatStatuses = async () => {
    const messagesContainer = document.getElementById('messagesContainer');
    if (!messagesContainer) return;
    
    // Get all unique user IDs from messages
    const avatars = messagesContainer.querySelectorAll('img[onclick*="showUserProfile"]');
    const userIds = new Set();
    
    avatars.forEach(avatar => {
      const onclick = avatar.getAttribute('onclick');
      const match = onclick?.match(/showUserProfile\('([^']+)'/);
      if (match) userIds.add(match[1]);
    });
    
    // Update status for each user
    for (const userId of userIds) {
      const isOnline = await window.getUserOnlineStatus(userId);
      const statusColor = isOnline ? '#34a853' : '#9e9e9e';
      
      // Update CSS variable
      document.documentElement.style.setProperty(`--status-color-${userId}`, statusColor);
    }
  };
  
  // Poll every 15 seconds to catch offline transitions
  window.chatStatusInterval = setInterval(updateAllChatStatuses, 15000);
  
  window.chatSubscription = supabaseClient
    .channel(`chat:${eventId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `event_id=eq.${eventId}`
      },
(payload) => {
        const messagesContainer = document.getElementById('messagesContainer');
        if (!messagesContainer) {
          console.error('❌ Messages container not found');
          return;
        }
        
        const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
        const isOwnMessage = payload.new.user_id === currentUserId;
        
// 🆕 If this is our own message, check for temporary message first
        if (isOwnMessage) {
          // Look for temp messages from us
          const tempMessages = messagesContainer.querySelectorAll('[data-message-id^="temp_"]');

          if (tempMessages.length > 0) {
            const tempMessage = tempMessages[tempMessages.length - 1];
            const tempMsgId = tempMessage.getAttribute('data-message-id');

            // 1. Update the temp message's ID to the real ID
            tempMessage.setAttribute('data-message-id', payload.new.id);
            
            // 2. Update status element ID
            const oldStatus = document.getElementById(`status_${tempMsgId}`);
            if (oldStatus) {
              oldStatus.id = `status_${payload.new.id}`;
            }
            
            // 3. Update timestamp element ID
            const oldTimestamp = document.getElementById(`timestamp_${tempMsgId}`);
            if (oldTimestamp) {
              oldTimestamp.id = `timestamp_${payload.new.id}`;
            }

            // 3a. Update reactions container ID
            const oldReactions = document.getElementById(`reactions_${tempMsgId}`);
            if (oldReactions) {
              oldReactions.id = `reactions_${payload.new.id}`;
            }

// 4. Apply grouping - recalculate for ALL own messages
const allContainers = Array.from(messagesContainer.querySelectorAll('div[style*="flex-direction"]'));
const currentContainer = tempMessage.closest('div[style*="flex-direction"]');

// Recalculate grouping for OWN messages only
allContainers.forEach((container, index) => {
  const isOwn = container.style.flexDirection.includes('row-reverse');
  
  // ONLY process OWN messages when replacing temp message
  if (!isOwn) return;
  
  const msgEl = container.querySelector('[data-message-id]');
  if (!msgEl) return;
  
  const msgId = msgEl.getAttribute('data-message-id');
  
// Check if NEXT message is also from the current user (using user_id)
const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
const currentMsgUserId = msgEl.getAttribute('data-user-id');

let nextIsFromSameUser = false;
for (let i = index + 1; i < allContainers.length; i++) {
  const nextContainer = allContainers[i];
  const nextMsgEl = nextContainer.querySelector('[data-message-id]');
  if (!nextMsgEl) continue;
  
  const nextMsgUserId = nextMsgEl.getAttribute('data-user-id');
  
  // Check if next message is from the same user
  if (nextMsgUserId === currentMsgUserId) {
    nextIsFromSameUser = true;
    break;
  }
}
  
// Update this message's grouping
if (nextIsFromSameUser) {
  // Not last in group - hide timestamp, small margin
  container.style.marginBottom = '2px';
  
  const timestamp = document.getElementById(`timestamp_${msgId}`);
  if (timestamp) {
    timestamp.style.display = 'none';
  }

  // Clear status icon (only for own messages)
  const status = document.getElementById(`status_${msgId}`);
  if (status) {
    status.innerHTML = '';
  }
} else {
  // Last in group - show timestamp, large margin
  container.style.marginBottom = '12px';
    
    const timestamp = document.getElementById(`timestamp_${msgId}`);
    if (timestamp) {
      timestamp.style.display = 'flex';
    }
    
// Update status icon for the current message only
    const status = document.getElementById(`status_${msgId}`);
    if (status && msgId === payload.new.id) {
      // This is the message that just got real data
      const hasBeenRead = payload.new.read_by && payload.new.read_by.length > 0;
      
      if (hasBeenRead) {
        status.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="margin-left: 4px; flex-shrink: 0;">
            <path d="M1 12l5 5L17 6" stroke="#00BCD4" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M8 12l5 5L24 6" stroke="#00BCD4" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
      } else if (payload.new.delivered_at) {
        status.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="margin-left: 4px; flex-shrink: 0;">
            <path d="M1 12l5 5L17 6" stroke="#9e9e9e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M8 12l5 5L24 6" stroke="#9e9e9e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
      } else if (payload.new.sent_at) {
        status.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="margin-left: 4px; flex-shrink: 0;">
            <path d="M4 12l6 6L20 6" stroke="#9e9e9e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
      }
    }
    // For RECEIVED messages: avatar should already be there (do nothing)
  }
});

            
// 5. Clear checkmarks from ALL previous messages (except failed ones)
            const allOwnMessages = messagesContainer.querySelectorAll('[data-message-id]');
            allOwnMessages.forEach(msgEl => {
              const msgContainer = msgEl.closest('div[style*="flex-direction"]');
              if (msgContainer && msgContainer.style.flexDirection.includes('row-reverse')) {
                const msgId = msgEl.getAttribute('data-message-id');
                if (msgId !== payload.new.id && !msgId.startsWith('temp_')) { // Don't clear current or temp messages
                  const statusEl = document.getElementById(`status_${msgId}`);
                  if (statusEl) {
                    // Clear all checkmarks (sent/delivered/read), but keep error icons
                    const hasErrorIcon = statusEl.innerHTML.includes('stroke="#ea4335"');
                    if (!hasErrorIcon) {
                      statusEl.innerHTML = '';
                    }
                  }
                }
              }
            });


// 6. Add checkmark to the current message (the new last message)
const currentStatusEl = document.getElementById(`status_${payload.new.id}`);
const currentTimestampEl = document.getElementById(`timestamp_${payload.new.id}`);

if (currentStatusEl) {
  // Make sure timestamp is visible
  if (currentTimestampEl) {
    currentTimestampEl.style.display = 'flex';
  }
  
  // Add the appropriate status icon
  const hasBeenRead = payload.new.read_by && payload.new.read_by.length > 0;
  
  if (hasBeenRead) {
    currentStatusEl.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="margin-left: 4px; flex-shrink: 0;">
        <path d="M1 12l5 5L17 6" stroke="#00BCD4" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M8 12l5 5L24 6" stroke="#00BCD4" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
  } else if (payload.new.delivered_at) {
    currentStatusEl.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="margin-left: 4px; flex-shrink: 0;">
        <path d="M1 12l5 5L17 6" stroke="#9e9e9e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M8 12l5 5L24 6" stroke="#9e9e9e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
  } else if (payload.new.sent_at) {
    currentStatusEl.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="margin-left: 4px; flex-shrink: 0;">
        <path d="M4 12l6 6L20 6" stroke="#9e9e9e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
  }
}

return; // Don't add a new message
          }
        }
        
        // Check if message already exists (by real ID)
        const existingMessage = document.querySelector(`[data-message-id="${payload.new.id}"]`);
        if (existingMessage) {
          return;
        }
        
// If this is a received message, recalculate grouping for all received messages from this user
if (!isOwnMessage) {
  // Wait for next paint cycle
  requestAnimationFrame(() => {
    const messagesContainer = document.getElementById('messagesContainer');
    const allChildren = Array.from(messagesContainer.children);
    
    // Find all message containers from this sender
    const senderMessages = [];
    allChildren.forEach((child, childIndex) => {
      const msgEl = child.querySelector('[data-message-id]');
      if (msgEl) {
        const msgUserId = msgEl.getAttribute('data-user-id');
        if (msgUserId === payload.new.user_id) {
          senderMessages.push({
            container: child,
            msgEl,
            msgId: msgEl.getAttribute('data-message-id'),
            childIndex
          });
        }
      }
    });
    
    // Process each message
    senderMessages.forEach((msg, senderIndex) => {
      let isLastInGroup = true;
      
      if (senderIndex < senderMessages.length - 1) {
        const nextMsg = senderMessages[senderIndex + 1];
        const currentIndex = msg.childIndex;
        const nextIndex = nextMsg.childIndex;
        
        if (nextIndex === currentIndex + 1) {
          isLastInGroup = false;
        } else {
          const elementsBetween = allChildren.slice(currentIndex + 1, nextIndex);
          const hasBreaker = elementsBetween.some(el => 
            el.querySelector('span[style*="background: #e8eaed"]') || 
            (el.querySelector('[data-message-id]') && 
             el.querySelector('[data-message-id]').getAttribute('data-user-id') !== payload.new.user_id)
          );
          
          isLastInGroup = hasBreaker;
        }
      }
      
      // Handle avatar
      if (!isLastInGroup) {
        // Find and hide avatar - search ALL children
        let found = false;
        
        for (const child of msg.container.children) {
          if (child.tagName === 'STYLE') continue;
          
          const img = child.querySelector('img');
          if (img) {
            child.outerHTML = '<div style="width: 32px; flex-shrink: 0;"></div>';
            found = true;
            break;
          }
        }
      }
      
      // Handle spacing and timestamp
      if (isLastInGroup) {
        msg.container.style.marginBottom = '12px';
        const timestamp = document.getElementById(`timestamp_${msg.msgId}`);
        if (timestamp) timestamp.style.display = 'flex';
      } else {
        msg.container.style.marginBottom = '2px';
        const timestamp = document.getElementById(`timestamp_${msg.msgId}`);
        if (timestamp) timestamp.style.display = 'none';
      }
    });
  }); // ← requestAnimationFrame closing here
  
  requestAnimationFrame(() => {
    const messagesContainer = document.getElementById('messagesContainer');
    if (!messagesContainer) return;
    
    const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
    const allMessages = messagesContainer.querySelectorAll('[data-message-id]');
    
    allMessages.forEach(msgEl => {
      const userId = msgEl.getAttribute('data-user-id');
      
      // Only process our own messages
      if (userId === currentUserId) {
        const msgId = msgEl.getAttribute('data-message-id');
        const statusEl = document.getElementById(`status_${msgId}`);
        
        if (statusEl && statusEl.innerHTML !== '') {
          statusEl.innerHTML = '';
        }
      }
    });
  });
}
        
        // Remove empty state if present
        const emptyState = messagesContainer.querySelector('p');
        if (emptyState && emptyState.textContent.includes('No messages yet')) {
          messagesContainer.innerHTML = '';
        }
        
        // Check if we need to add a date divider
        const newMessageDate = new Date(payload.new.created_at);
        const existingDividers = Array.from(messagesContainer.querySelectorAll('div[style*="text-align: center"] span'));
        const hasDateDivider = existingDividers.some(divider => 
          divider.textContent === formatDateDivider(payload.new.created_at)
        );
        
        if (!hasDateDivider) {
          const dateDivider = document.createElement('div');
          dateDivider.style.cssText = 'text-align: center; margin: 8px 0;';
          dateDivider.innerHTML = `
            <span style="
              font-size: 11px;
              color: #5f6368;
              background: #e8eaed;
              padding: 4px 12px;
              border-radius: 12px;
            ">${formatDateDivider(payload.new.created_at)}</span>
          `;
          messagesContainer.appendChild(dateDivider);
        }
        
        // Get all existing message containers
        const allMessageContainers = Array.from(messagesContainer.querySelectorAll('div[style*="flex-direction"]'));
        
        let isFirstInGroup = true;
        let isLastInGroup = true;
        
        if (allMessageContainers.length > 0) {
          const lastContainer = allMessageContainers[allMessageContainers.length - 1];
          const lastMessageEl = lastContainer.querySelector('[data-message-id]');
          
          if (lastMessageEl) {
            const lastMsgId = lastMessageEl.getAttribute('data-message-id');
            
// Check if from same user by comparing user IDs
const lastUserId = lastMessageEl.getAttribute('data-user-id');
const newUserId = payload.new.user_id;
const isSameUser = lastUserId === newUserId;
            
            if (isSameUser) {
              isFirstInGroup = false;
              
              // Update previous message: it's no longer the last in the group
              lastContainer.style.marginBottom = '2px';
              
              // Hide timestamp on previous message
              const prevTimestamp = document.getElementById(`timestamp_${lastMsgId}`);
              if (prevTimestamp) {
                prevTimestamp.style.display = 'none';
              }
              
              // Hide status icon on previous message (if it was own message)
              const prevStatus = document.getElementById(`status_${lastMsgId}`);
              if (prevStatus) {
                prevStatus.innerHTML = ''; // Clear the status icon
              }
              
              // If not own message, replace avatar with spacer
if (!isOwnMessage) {
                const prevAvatarContainer = lastContainer.querySelector('div[style*="position: relative"]');
                if (prevAvatarContainer && prevAvatarContainer.querySelector('img')) {
                  prevAvatarContainer.outerHTML = '<div style="width: 32px; flex-shrink: 0;"></div>';
                }
              }
            }
          }
        }
        
        // Add new message
        const newMessageEl = createMessageElement(payload.new, isOwnMessage, isLastInGroup, isFirstInGroup, false);
        messagesContainer.appendChild(newMessageEl);
        
        // Scroll to bottom smoothly
        messagesContainer.scrollTo({
          top: messagesContainer.scrollHeight,
          behavior: 'smooth'
        });

        // Mark messages as read if this is not our own message
if (!isOwnMessage) {
  setTimeout(() => {
    window.markMessagesAsRead(eventId);
  }, 500); // Small delay to ensure message is in DB
}
      }
    )
.on(
  'postgres_changes',
  {
    event: 'UPDATE',
    schema: 'public',
    table: 'messages',
    filter: `event_id=eq.${eventId}`
  },
(payload) => {

// Check if message was deleted
if (payload.new.is_deleted) {

  const messageBubble = document.querySelector(`[data-message-id="${payload.new.id}"]`);
  if (messageBubble) {
    // Update the message content
    const messageText = messageBubble.querySelector('p');
    if (messageText) {
      messageText.innerHTML = '';
      messageText.textContent = 'Message deleted';
      messageText.style.fontStyle = 'italic';
      messageText.style.color = '#9e9e9e';
    }
    
    // Remove hover actions
    const hoverActions = messageBubble.querySelector('.message-hover-actions');
    if (hoverActions) hoverActions.remove();
    
    // Remove reactions
    const reactionsContainer = document.getElementById(`reactions_${payload.new.id}`);
    if (reactionsContainer) reactionsContainer.remove();
    
    // Remove click handlers
    messageBubble.onclick = null;
    messageBubble.ontouchstart = null;
    messageBubble.ontouchend = null;
    
    // Change appearance
    messageBubble.style.background = '#f1f3f4';
    messageBubble.style.color = '#9e9e9e';
    messageBubble.style.cursor = 'default';
    
    // Remove bubble hover effects
    messageBubble.classList.remove('message-bubble-own', 'message-bubble-other');
  }
  
  return; // Don't process further
}

    // 🆕 Handle message text edits
  const messageBubble = document.querySelector(`[data-message-id="${payload.new.id}"]`);
  if (messageBubble && payload.old && payload.new.message !== payload.old.message) {

    const messageText = messageBubble.querySelector('p');
    if (messageText) {
      // Check if own message for proper color
      const isOwnMessage = messageBubble.classList.contains('message-bubble-own');
      
      // Clear current content
      messageText.innerHTML = '';
      
      // Add updated text
      const textNode = document.createTextNode(payload.new.message);
      messageText.appendChild(textNode);
      
      // Add edited indicator if message was edited
      if (payload.new.edited_at) {
        const editedSpan = document.createElement('span');
        editedSpan.className = 'edited-indicator';
        editedSpan.textContent = ' (edited)';
        editedSpan.style.cssText = `
          font-size: 11px;
          color: ${isOwnMessage ? 'rgba(255,255,255,0.7)' : '#5f6368'};
          margin-left: 6px;
          font-style: italic;
        `;
        messageText.appendChild(editedSpan);
      }
    }
  }
  
// 🆕 Add a small delay to let the DOM settle
setTimeout(() => {
  // Update status icon based on read/delivered/sent state
  const statusElement = document.getElementById(`status_${payload.new.id}`);
  if (statusElement) {
    const messagesContainer = document.getElementById('messagesContainer');
    const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
    const isOwnMessage = payload.new.user_id === currentUserId;
    
    let shouldUpdateStatus = true; // Flag to control whether to update
    
if (isOwnMessage) {
  // Find the last own message by user_id
  const allMessageElements = messagesContainer.querySelectorAll('[data-message-id]');
  let lastOwnMessageId = null;
  
  // Go through all messages in reverse to find the last one from current user
  Array.from(allMessageElements).reverse().forEach(msgEl => {
    if (lastOwnMessageId) return; // Already found it
    
    const userId = msgEl.getAttribute('data-user-id');
    if (userId === currentUserId) {
      lastOwnMessageId = msgEl.getAttribute('data-message-id');
    }
  });
  
  // Check if THIS message is the last own message
  const isLastOwnMessage = payload.new.id === lastOwnMessageId;

  
  if (!isLastOwnMessage) {
    // Not the last message, don't show checkmark
    statusElement.innerHTML = '';
    shouldUpdateStatus = false;
  }
}
    
    // Only update if flag is true (i.e., not own message OR is last own message)
    if (shouldUpdateStatus) {
      const hasBeenRead = payload.new.read_by && payload.new.read_by.length > 0;
      
      if (hasBeenRead) {
        // Read - double checkmark BLUE
        statusElement.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="margin-left: 4px; flex-shrink: 0;">
            <path d="M1 12l5 5L17 6" stroke="#00BCD4" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M8 12l5 5L24 6" stroke="#00BCD4" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
      } else if (payload.new.delivered_at) {
        // Delivered - double checkmark GREY
        statusElement.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="margin-left: 4px; flex-shrink: 0;">
            <path d="M1 12l5 5L17 6" stroke="#9e9e9e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M8 12l5 5L24 6" stroke="#9e9e9e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
      } else if (payload.new.sent_at) {
        // Sent - single checkmark grey
        statusElement.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="margin-left: 4px; flex-shrink: 0;">
            <path d="M4 12l6 6L20 6" stroke="#9e9e9e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
      }
    }
  }
  
// Update reactions if changed
if (payload.new.reactions) {
  window.updateReactionsUI(payload.new.id, payload.new.reactions);
}

// Update the message in global storage
if (window.currentChatMessages) {
  const msgIndex = window.currentChatMessages.findIndex(m => m.id === payload.new.id);
  if (msgIndex !== -1) {
    window.currentChatMessages[msgIndex] = payload.new;
  }
}
}, 50);
}
)
.on(
  'postgres_changes',
  {
    event: 'UPDATE',
    schema: 'public',
    table: 'users'
  },
  async (payload) => {

    // Update status color for this user
    const userId = payload.new.id;
    const isOnline = await window.getUserOnlineStatus(userId);
    const statusColor = isOnline ? '#34a853' : '#9e9e9e';
    
    // Update CSS variable
    document.documentElement.style.setProperty(`--status-color-${userId}`, statusColor);

  }
)
.on(
  'postgres_changes',
  {
    event: 'UPDATE',
    schema: 'public',
    table: 'events',
    filter: `event_id=eq.${eventId}`
  },
  async (payload) => {

    const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
    const isOrganizer = payload.new.creator_id === currentUserId;
    
    // Update checkbox for organizer
    const checkbox = document.getElementById('chatEnabledCheckbox');
    if (checkbox && isOrganizer) {
      checkbox.checked = payload.new.chat_enabled;
    }
    
    // Update input area for participants without closing chat
    if (!isOrganizer && payload.new.chat_enabled !== undefined) {
      const chatPanel = document.getElementById('chatPanel');
      if (!chatPanel) return;
      
// Find the exact message input container or its wrapper
let inputContainer = document.getElementById('messageInputContainer');
if (!inputContainer) {
  // If blocked, find the blocked container
  inputContainer = document.querySelector('#chatPanel > div:last-child');
}

if (!inputContainer) return;
      
      if (payload.new.chat_enabled) {
  // Enable messaging - show active input
  const newContainer = document.createElement('div');
  newContainer.innerHTML = `
  <div id="messageInputContainer" style="
    padding: 16px;
    border-top: 1px solid #e0e0e0;
    flex-shrink: 0;
    background: white;
  ">
    <div style="
      display: flex;
      gap: 8px;
      align-items: flex-end;
    ">
      <textarea
        id="messageInput"
        placeholder="Type a message..."
        rows="1"
        maxlength="1000"
        style="
          flex: 1;
          padding: 12px 16px;
          border: 1px solid #dadce0;
          border-radius: 24px;
          font-size: 14px;
          font-family: inherit;
          resize: none;
          max-height: 120px;
          line-height: 1.4;
          outline: none;
        "
        oninput="this.style.height = 'auto'; this.style.height = Math.min(this.scrollHeight, 120) + 'px'"
        onkeydown="if(event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); window.sendMessage('${eventId}'); }"
      ></textarea>
      
      <button
        id="emojiPickerButton"
        onclick="window.toggleEmojiPicker()"
        style="
          width: 40px;
          height: 40px;
          border: none;
          border-radius: 50%;
          background: transparent;
          color: #5f6368;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          flex-shrink: 0;
        "
        onmouseover="this.style.background='#f1f3f4'"
        onmouseout="this.style.background='transparent'"
        title="Add emoji"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
          <circle cx="9" cy="9" r="0.5" fill="currentColor"></circle>
          <circle cx="15" cy="9" r="0.5" fill="currentColor"></circle>
        </svg>
      </button>
      
      <button
        id="sendButton"
        onclick="window.sendMessage('${eventId}')"
        style="
          width: 40px;
          height: 40px;
          border: none;
          border-radius: 50%;
          background: transparent;
          color: #5f6368;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          flex-shrink: 0;
        "
        onmouseover="this.style.background='#f1f3f4'"
        onmouseout="this.style.background='transparent'"
        title="Send message"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
        </svg>
      </button>
    </div>
    
    <div id="emojiPicker" style="
      display: none;
      position: absolute;
      bottom: 80px;
      left: 16px;
      right: 16px;
      max-width: 350px;
      background: white;
      border-radius: 12px;
      box-shadow: 0 4px 16px rgba(0,0,0,0.2);
      padding: 12px;
      z-index: 10002;
      max-height: 300px;
      overflow-y: auto;
    ">
      <div style="
        display: grid;
        grid-template-columns: repeat(8, 1fr);
        gap: 8px;
      ">
        ${['😀','😃','😄','😁','😆','😅','🤣','😂','🙂','🙃','😉','😊','😇','🥰','😍','🤩','😘','😗','😚','😙','🥲','😋','😛','😜','🤪','😝','🤗','🤭','🤫','🤔','🤐','🤨','😐','😑','😶','😏','😒','🙄','😬','🤥','😌','😔','😪','🤤','😴','😷','🤒','🤕','🤢','🤮','🤧','🥵','🥶','🥴','😵','🤯','🤠','🥳','🥸','😎','🤓','🧐','😕','😟','🙁','☹️','😮','😯','😲','😳','🥺','😦','😧','😨','😰','😥','😢','😭','😱','😖','😣','😞','😓','😩','😫','🥱','😤','😡','😠','🤬','😈','👿','💀','☠️','💩','🤡','👹','👺','👻','👽','👾','🤖','😺','😸','😹','😻','😼','😽','🙀','😿','😾','👋','🤚','🖐️','✋','🖖','👌','🤌','🤏','✌️','🤞','🤟','🤘','🤙','👈','👉','👆','🖕','👇','☝️','👍','👎','✊','👊','🤛','🤜','👏','🙌','👐','🤲','🤝','🙏','✍️','💪','🦾','🦿','🦵','🦶','👂','🦻','👃','🧠','🫀','🫁','🦷','🦴','👀','👁️','👅','👄','💋','🩸','❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗','💖','💘','💝','💟','☮️','✝️','☪️','🕉️','☸️','✡️','🔯','🕎','☯️','☦️','🛐','⛎','♈','♉','♊','♋','♌','♍','♎','♏','♐','♑','♒','♓','🆔','⚛️','🉑','☢️','☣️','📴','📳','🈶','🈚','🈸','🈺','🈷️','✴️','🆚','💮','🉐','㊙️','㊗️','🈴','🈵','🈹','🈲','🅰️','🅱️','🆎','🆑','🅾️','🆘','❌','⭕','🛑','⛔','📛','🚫','💯','💢','♨️','🚷','🚯','🚳','🚱','🔞','📵','🚭','❗','❕','❓','❔','‼️','⁉️','🔅','🔆','〽️','⚠️','🚸','🔱','⚜️','🔰','♻️','✅','🈯','💹','❇️','✳️','❎','🌐','💠','Ⓜ️','🌀','💤','🏧','🚾','♿','🅿️','🈳','🈂️','🛂','🛃','🛄','🛅','🚹','🚺','🚼','⚧️','🚻','🚮','🎦','📶','🈁','🔣','ℹ️','🔤','🔡','🔠','🆖','🆗','🆙','🆒','🆕','🆓','0️⃣','1️⃣','2️⃣','3️⃣','4️⃣','5️⃣','6️⃣','7️⃣','8️⃣','9️⃣','🔟','🔢','#️⃣','*️⃣','⏏️','▶️','⏸️','⏯️','⏹️','⏺️','⏭️','⏮️','⏩','⏪','⏫','⏬','◀️','🔼','🔽','➡️','⬅️','⬆️','⬇️','↗️','↘️','↙️','↖️','↕️','↔️','↪️','↩️','⤴️','⤵️','🔀','🔁','🔂','🔄','🔃','🎵','🎶','➕','➖','➗','✖️','♾️','💲','💱','™️','©️','®️','〰️','➰','➿','🔚','🔙','🔛','🔝','🔜','✔️','☑️','🔘','🔴','🟠','🟡','🟢','🔵','🟣','⚫','⚪','🟤','🔺','🔻','🔸','🔹','🔶','🔷','🔳','🔲','▪️','▫️','◾','◽','◼️','◻️','🟥','🟧','🟨','🟩','🟦','🟪','⬛','⬜','🟫','🔈','🔇','🔉','🔊','🔔','🔕','📣','📢','👁️‍🗨️','💬','💭','🗯️','♠️','♣️','♥️','♦️','🃏','🎴','🀄','🕐','🕑','🕒','🕓','🕔','🕕','🕖','🕗','🕘','🕙','🕚','🕛','🕜','🕝','🕞','🕟','🕠','🕡','🕢','🕣','🕤','🕥','🕦','🕧'].map(emoji => 
          `<button onclick="window.insertEmoji('${emoji}')" style="
            width: 32px;
            height: 32px;
            border: none;
            background: transparent;
            font-size: 24px;
            cursor: pointer;
            border-radius: 4px;
            transition: background 0.2s;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
          " onmouseover="this.style.background='#f1f3f4'" onmouseout="this.style.background='transparent'">${emoji}</button>`
        ).join('')}
      </div>
    </div>
  </div>
`;

inputContainer.replaceWith(newContainer.firstElementChild);
        
        // Focus on the new input
        setTimeout(() => {
          document.getElementById('messageInput')?.focus();
        }, 100);
        
      } else {
  // Disable messaging - show blocked input
  const newContainer = document.createElement('div');
  newContainer.innerHTML = `
          <div style="
            padding: 16px;
            border-top: 1px solid #e0e0e0;
            flex-shrink: 0;
            background: white;
            position: relative;
          ">
            <div style="
              filter: blur(3px);
              pointer-events: none;
              opacity: 0.5;
            ">
              <div style="
                display: flex;
                gap: 8px;
                align-items: flex-end;
              ">
                <textarea
                  placeholder="Type a message..."
                  rows="1"
                  disabled
                  style="
                    flex: 1;
                    padding: 12px 16px;
                    border: 1px solid #dadce0;
                    border-radius: 24px;
                    font-size: 14px;
                    font-family: inherit;
                    resize: none;
                    background: #f5f5f5;
                  "
                ></textarea>
                <button disabled style="
                  width: 40px;
                  height: 40px;
                  border: none;
                  border-radius: 50%;
                  background: transparent;
                  color: #5f6368;
                ">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                  </svg>
                </button>
                <button disabled style="
                  width: 40px;
                  height: 40px;
                  border: none;
                  border-radius: 50%;
                  background: transparent;
                  color: #5f6368;
                ">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                  </svg>
                </button>
              </div>
            </div>
<div style="
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
">
  <span style="
    font-size: 14px;
    font-weight: 600;
    color: #202124;
    text-shadow: 0 1px 3px rgba(255,255,255,0.8);
  ">Messaging disabled</span>
</div>
          </div>
        `;

        inputContainer.replaceWith(newContainer.firstElementChild);
      }
    }
  }
)
.on('broadcast', { event: 'reaction_update' }, ({ payload }) => {
  if (payload?.messageId && payload?.reactions !== undefined) {
    window.updateReactionsUI(payload.messageId, payload.reactions || {});
  }
})
    .subscribe((status) => {});
};

// Set up global message listener (call this ONCE when user logs in)
export const setupGlobalMessageListener = () => {
  const supabaseClient = window.supabase || supabase;
  
  // Clean up existing listener first
  if (window.globalMessageListener) {
    supabaseClient.removeChannel(window.globalMessageListener);
    window.globalMessageListener = null;
  }
  
  window.globalMessageListener = supabaseClient
    .channel('all-messages')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages'
      },
      async (payload) => {

        // Don't update indicator if message is in currently open chat
        if (payload.new.event_id === window.currentOpenChatId) {
          return;
        }

        // Check if chats list is open
        const chatsPanel = document.getElementById('chatsListPanel');
        if (chatsPanel) {
          await window.updateChatRow(payload.new);
        } else {
          window.checkUnreadMessagesQuick();
        }
      }
    )
    .subscribe((status) => {});
};

// Toggle emoji picker
export const toggleEmojiPicker = () => {
  const emojiPicker = document.getElementById('emojiPicker');
  if (!emojiPicker) return;
  
  if (emojiPicker.style.display === 'none' || !emojiPicker.style.display) {
    emojiPicker.style.display = 'block';
  } else {
    emojiPicker.style.display = 'none';
  }
};

// Insert emoji into message input
export const insertEmoji = (emoji) => {
  const messageInput = document.getElementById('messageInput');
  if (!messageInput) return;
  
  const cursorPos = messageInput.selectionStart;
  const textBefore = messageInput.value.substring(0, cursorPos);
  const textAfter = messageInput.value.substring(cursorPos);
  
  messageInput.value = textBefore + emoji + textAfter;
  messageInput.focus();
  messageInput.selectionStart = messageInput.selectionEnd = cursorPos + emoji.length;
  
  // Hide emoji picker
  const emojiPicker = document.getElementById('emojiPicker');
  if (emojiPicker) emojiPicker.style.display = 'none';
  
  // Trigger resize
  messageInput.style.height = 'auto';
  messageInput.style.height = Math.min(messageInput.scrollHeight, 120) + 'px';
};

// Close emoji picker when clicking outside
document.addEventListener('click', (e) => {
  const emojiPicker = document.getElementById('emojiPicker');
  const emojiButton = document.getElementById('emojiPickerButton');
  
  if (emojiPicker && emojiButton && 
      !emojiPicker.contains(e.target) && 
      !emojiButton.contains(e.target)) {
    emojiPicker.style.display = 'none';
  }
});

// Show message options (right-click context menu)
export const showMessageOptions = (event, messageId, messageUserId, isOwnMessage) => {
  event.preventDefault();
  
  // Remove existing menu
  const existingMenu = document.getElementById('messageContextMenu');
  if (existingMenu) existingMenu.remove();
  
  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  
  // Create context menu
  const menu = document.createElement('div');
  menu.id = 'messageContextMenu';
  menu.style.cssText = `
    position: fixed;
    left: ${event.clientX}px;
    top: ${event.clientY}px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.2);
    padding: 4px 0;
    z-index: 10003;
    min-width: 150px;
  `;
  
  const options = [];
  
  // Add reaction option for all messages
  options.push({
    icon: '👍',
    label: 'React',
    onClick: () => window.showReactionPicker(messageId, event.clientX, event.clientY)
  });
  
  // Add edit/delete options only for own messages
  if (isOwnMessage && messageUserId === currentUserId) {
    options.push({
      icon: '✏️',
      label: 'Edit',
      onClick: () => window.editMessage(messageId)
    });
    
    options.push({
      icon: '🗑️',
      label: 'Delete',
      onClick: () => window.deleteMessage(messageId),
      danger: true
    });
  }
  
  // Build menu HTML
  menu.innerHTML = options.map(option => `
    <div 
      onclick="${option.onClick.toString().match(/\{([\s\S]*)\}/)[0]}" 
      style="
        padding: 10px 16px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 12px;
        ${option.danger ? 'color: #ea4335;' : 'color: #202124;'}
        transition: background 0.2s;
      "
      onmouseover="this.style.background='#f1f3f4'"
      onmouseout="this.style.background='white'"
    >
      <span style="font-size: 18px;">${option.icon}</span>
      <span style="font-size: 14px; font-weight: 500;">${option.label}</span>
    </div>
  `).join('');
  
  document.body.appendChild(menu);
  
  // Close menu when clicking outside
  setTimeout(() => {
    document.addEventListener('click', function closeMenu() {
      menu.remove();
      document.removeEventListener('click', closeMenu);
    });
  }, 100);
};

// Show reaction picker
export const showReactionPicker = (messageId, x, y) => {
  // Close context menu
  const contextMenu = document.getElementById('messageContextMenu');
  if (contextMenu) contextMenu.remove();
  
  // Remove existing picker
  const existingPicker = document.getElementById('reactionPicker');
  if (existingPicker) existingPicker.remove();
  
  const picker = document.createElement('div');
  picker.id = 'reactionPicker';
  picker.style.cssText = `
    position: fixed;
    left: ${x}px;
    top: ${y}px;
    background: white;
    border-radius: 24px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.2);
    padding: 8px 12px;
    z-index: 10003;
    display: flex;
    gap: 4px;
  `;
  
  const reactions = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
  
  picker.innerHTML = reactions.map(emoji => `
    <button
      onclick="window.addReaction('${messageId}', '${emoji}')"
      style="
        width: 40px;
        height: 40px;
        border: none;
        background: transparent;
        font-size: 24px;
        cursor: pointer;
        border-radius: 50%;
        transition: all 0.2s;
      "
      onmouseover="this.style.background='#f1f3f4'; this.style.transform='scale(1.2)'"
      onmouseout="this.style.background='transparent'; this.style.transform='scale(1)'"
    >${emoji}</button>
  `).join('');
  
  document.body.appendChild(picker);
  
  // Close picker when clicking outside
  setTimeout(() => {
    document.addEventListener('click', function closePicker() {
      picker.remove();
      document.removeEventListener('click', closePicker);
    });
  }, 100);
};

// Add reaction to message
export const addReaction = async (messageId, emoji) => {
  // Close reaction picker
  const picker = document.getElementById('reactionPicker');
  if (picker) picker.remove();
  
  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  
  // 🆕 Check if this is user's own message
  const messageEl = document.querySelector(`[data-message-id="${messageId}"]`);
  if (messageEl) {
    const messageUserId = messageEl.getAttribute('data-user-id');
    if (messageUserId === currentUserId) {
      return;
    }
  }
  
  try {
    const supabaseClient = window.supabase || supabase;
    
    // Get current reactions
    const { data: message } = await supabaseClient
      .from('messages')
      .select('reactions')
      .eq('id', messageId)
      .single();
    
    let reactions = message?.reactions || {};
    
    // Toggle reaction (add if not present, remove if present)
    if (!reactions[emoji]) {
      reactions[emoji] = [];
    }
    
    const userIndex = reactions[emoji].indexOf(currentUserId);
    if (userIndex > -1) {
      reactions[emoji].splice(userIndex, 1);
      if (reactions[emoji].length === 0) {
        delete reactions[emoji];
      }
    } else {
      reactions[emoji].push(currentUserId);
    }
    
    // Update in database
    await supabaseClient
      .from('messages')
      .update({ reactions })
      .eq('id', messageId);

    // Broadcast reaction change to all chat participants in real-time
    // Must use the already-subscribed channel, not a new channel reference
    if (window.chatSubscription) {
      window.chatSubscription.send({
        type: 'broadcast',
        event: 'reaction_update',
        payload: { messageId, reactions },
      });
    }

    // Update UI for the current user immediately
    window.updateReactionsUI(messageId, reactions);
    
  } catch (error) {
    console.error('Error adding reaction:', error);
  }
};

// Hover timer for desktop
let reactionHoverTimer = null;
let reactionHoverElement = null;

export const startReactionHover = (messageId, emoji, element) => {
  // Clear any existing timer
  window.cancelReactionHover();
  
  // Store the element reference
  reactionHoverElement = element;
  
  // Start 800ms timer for hover
  reactionHoverTimer = setTimeout(() => {
    // Check if we're still hovering the same element
    if (reactionHoverElement && reactionHoverElement.matches(':hover')) {
      window.showReactionsDetails(messageId, emoji);
    }
    reactionHoverElement = null;
  }, 800);
};

export const cancelReactionHover = () => {
  if (reactionHoverTimer) {
    clearTimeout(reactionHoverTimer);
    reactionHoverTimer = null;
  }
  reactionHoverElement = null;
};

// Long press for mobile
let reactionLongPressTimer = null;
let reactionLongPressTriggered = false;

export const startReactionLongPress = (event, messageId, emoji) => {
  reactionLongPressTriggered = false;
  
  // Start 500ms timer for long press
  reactionLongPressTimer = setTimeout(() => {
    reactionLongPressTriggered = true;
    window.showReactionsDetails(messageId, emoji);
  }, 500);
};

export const cancelReactionLongPress = (messageId, emoji, canReact) => {
  if (reactionLongPressTimer) {
    clearTimeout(reactionLongPressTimer);
    reactionLongPressTimer = null;
  }
  // Quick tap (not a long press) → trigger reaction directly
  if (!reactionLongPressTriggered && canReact && messageId && emoji) {
    window.addReaction(messageId, emoji);
  }
  reactionLongPressTriggered = false;
};

// Update reactions UI
export const updateReactionsUI = (messageId, reactions) => {
  const reactionsContainer = document.getElementById(`reactions_${messageId}`);
  if (!reactionsContainer) return;
  
  if (!reactions || Object.keys(reactions).length === 0) {
    reactionsContainer.innerHTML = '';
    return;
  }
  
  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  
  // Check if this is user's own message
  const messageEl = document.querySelector(`[data-message-id="${messageId}"]`);
  const messageUserId = messageEl?.getAttribute('data-user-id');
  const isOwnMessage = messageUserId === currentUserId;
  
  reactionsContainer.innerHTML = Object.entries(reactions)
    .filter(([_, users]) => users.length > 0)
    .map(([emoji, users]) => {
      const hasReacted = users.includes(currentUserId);
      const reactionId = `reaction_${messageId}_${emoji}`;
      
      const canReact = !isOwnMessage && !window.currentChatIsArchived;
      return `
        <div
          id="${reactionId}"
          onclick="event.stopPropagation();${canReact ? ` window.addReaction('${messageId}', '${emoji}')` : ''}"
          style="
            background: ${hasReacted ? '#e3f2fd' : '#f1f3f4'};
            border: 1px solid ${hasReacted ? '#4285f4' : '#dadce0'};
            border-radius: 12px;
            padding: 4px 10px;
            display: flex;
            align-items: center;
            gap: 4px;
            cursor: ${canReact ? 'pointer' : 'default'};
            transition: all 0.2s;
            position: relative;
            min-height: 28px;
          "
${canReact ? `
  onmouseover="this.style.transform='scale(1.1)'; window.startReactionHover('${messageId}', '${emoji}', this)"
  onmouseout="this.style.transform='scale(1)'; window.cancelReactionHover()"
  onmouseleave="window.cancelReactionHover()"
` : `
  onmouseover="window.startReactionHover('${messageId}', '${emoji}', this)"
  onmouseout="window.cancelReactionHover()"
  onmouseleave="window.cancelReactionHover()"
`}
          ontouchstart="event.stopPropagation(); window.startReactionLongPress(event, '${messageId}', '${emoji}')"
          ontouchend="event.stopPropagation(); event.preventDefault(); window.cancelReactionLongPress('${messageId}', '${emoji}', ${canReact})"
          ontouchmove="event.stopPropagation(); window.cancelReactionLongPress()"
        >
          <span style="font-size: 14px;">${emoji}</span>
          <span style="font-size: 11px; font-weight: 600; color: ${hasReacted ? '#4285f4' : '#5f6368'};">${users.length}</span>
        </div>
      `;
    }).join('');
};

// Edit message
export const editMessage = async (messageId) => {
  // Close context menu
  const contextMenu = document.getElementById('messageContextMenu');
  if (contextMenu) contextMenu.remove();
  
  const messageElement = document.querySelector(`[data-message-id="${messageId}"] p`);
  if (!messageElement) return;
  
  const originalText = messageElement.textContent;
  
  // Replace with input
  const messageContainer = messageElement.parentElement;
  messageContainer.innerHTML = `
    <textarea
      id="edit_${messageId}"
      style="
        width: 100%;
        padding: 8px;
        border: 1px solid #4285f4;
        border-radius: 8px;
        font-size: 14px;
        font-family: inherit;
        resize: vertical;
        min-height: 60px;
        outline: none;
      "
    >${originalText}</textarea>
    <div style="
      display: flex;
      gap: 8px;
      margin-top: 8px;
      justify-content: flex-end;
    ">
      <button
        onclick="window.cancelEditMessage('${messageId}', '${originalText.replace(/'/g, "\\'")}')"
        style="
          padding: 6px 12px;
          background: #f1f3f4;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
        "
      >Cancel</button>
      <button
        onclick="window.saveEditMessage('${messageId}')"
        style="
          padding: 6px 12px;
          background: #4285f4;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
        "
      >Save</button>
    </div>
  `;
  
  document.getElementById(`edit_${messageId}`).focus();
};

// Cancel edit message
export const cancelEditMessage = (messageId, originalText) => {
  const messageContainer = document.querySelector(`[data-message-id="${messageId}"]`);
  if (!messageContainer) return;
  
  messageContainer.innerHTML = `
    <p style="
      margin: 0;
      font-size: 14px;
      line-height: 1.4;
      white-space: pre-wrap;
      word-break: break-word;
    ">${escapeHtml(originalText)}</p>
    <div id="reactions_${messageId}" style="
      display: flex;
      gap: 4px;
      flex-wrap: wrap;
      margin-top: 6px;
    "></div>
  `;
};

// Save edited message
export const saveEditMessage = async (messageId) => {
  const textarea = document.getElementById(`edit_${messageId}`);
  if (!textarea) return;
  
  const newText = textarea.value.trim();
  if (!newText) {
    window.showToast('Message cannot be empty', 'error');
    return;
  }
  
  try {
    const supabaseClient = window.supabase || supabase;
    
    await supabaseClient
      .from('messages')
      .update({ 
        message: newText,
        edited_at: new Date().toISOString()
      })
      .eq('id', messageId);
    
    const messageContainer = document.querySelector(`[data-message-id="${messageId}"]`);
    if (messageContainer) {
      messageContainer.innerHTML = `
        <p style="
          margin: 0;
          font-size: 14px;
          line-height: 1.4;
          white-space: pre-wrap;
          word-break: break-word;
        ">${escapeHtml(newText)}${newText !== textarea.dataset.original ? ' <span style="font-size: 11px; color: #5f6368;">(edited)</span>' : ''}</p>
        <div id="reactions_${messageId}" style="
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
          margin-top: 6px;
        "></div>
      `;
    }
    
  } catch (error) {
    console.error('Error editing message:', error);
    window.showToast('Failed to edit message', 'error');
  }
};

export const deleteMessage = async (messageId) => {
  try {
    const supabaseClient = window.supabase || supabase;
    
    // Mark message as deleted in database
    const { error } = await supabaseClient
      .from('messages')
      .update({ 
        is_deleted: true,
        message: '[Message deleted]'
      })
      .eq('id', messageId);
    
    if (error) {
      window.showToast('Failed to delete message', 'error');
      return;
    }
    
    // Update UI immediately (optimistic update)
    const messageBubble = document.querySelector(`[data-message-id="${messageId}"]`);
    if (messageBubble) {
      // Update the message content
      const messageText = messageBubble.querySelector('p');
      if (messageText) {
        messageText.innerHTML = '';
        messageText.textContent = 'Message deleted';
        messageText.style.fontStyle = 'italic';
        messageText.style.color = '#9e9e9e';
      }
      
      // Remove hover actions
      const hoverActions = messageBubble.querySelector('.message-hover-actions');
      if (hoverActions) hoverActions.remove();
      
      // Remove reactions
      const reactionsContainer = document.getElementById(`reactions_${messageId}`);
      if (reactionsContainer) reactionsContainer.remove();
      
      // Remove click handlers
      messageBubble.onclick = null;
      messageBubble.ontouchstart = null;
      messageBubble.ontouchend = null;
      
      // Change appearance
      messageBubble.style.background = '#f1f3f4';
      messageBubble.style.color = '#9e9e9e';
      messageBubble.style.cursor = 'default';
      
      // Remove bubble hover effects
      messageBubble.classList.remove('message-bubble-own', 'message-bubble-other');
    }
    
    // Update local message data
    if (window.currentChatMessages) {
      const msgIndex = window.currentChatMessages.findIndex(m => m.id === messageId);
      if (msgIndex !== -1) {
        window.currentChatMessages[msgIndex].is_deleted = true;
        window.currentChatMessages[msgIndex].message = 'Message deleted';
      }
    }
    
  } catch (error) {
    window.showToast('Failed to delete message', 'error');
  }
};

// Show reaction picker inline when clicking on other user's message
export const showReactionPickerInline = (messageId, event) => {
  event.stopPropagation();
  
  // Remove existing picker
  const existingPicker = document.getElementById('inlineReactionPicker');
  if (existingPicker) existingPicker.remove();
  
  const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
  if (!messageElement) return;
  
  const rect = messageElement.getBoundingClientRect();
  
  const picker = document.createElement('div');
  picker.id = 'inlineReactionPicker';
  picker.style.cssText = `
    position: fixed;
    left: ${rect.left + rect.width / 2 - 100}px;
    top: ${rect.top - 50}px;
    background: white;
    border-radius: 24px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.2);
    padding: 8px 12px;
    z-index: 10003;
    display: flex;
    gap: 4px;
    animation: fadeIn 0.2s ease-out;
  `;
  
  const reactions = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
  
  picker.innerHTML = `
    <style>
      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    </style>
    ${reactions.map(emoji => `
      <button
        onclick="window.addReaction('${messageId}', '${emoji}'); document.getElementById('inlineReactionPicker').remove();"
        style="
          width: 40px;
          height: 40px;
          border: none;
          background: transparent;
          font-size: 24px;
          cursor: pointer;
          border-radius: 50%;
          transition: all 0.2s;
        "
        onmouseover="this.style.background='#f1f3f4'; this.style.transform='scale(1.2)'"
        onmouseout="this.style.background='transparent'; this.style.transform='scale(1)'"
      >${emoji}</button>
    `).join('')}
  `;
  
  document.body.appendChild(picker);
  
  // Close picker when clicking outside
  setTimeout(() => {
    const closeHandler = (e) => {
      if (!picker.contains(e.target)) {
        picker.remove();
        document.removeEventListener('click', closeHandler);
      }
    };
    document.addEventListener('click', closeHandler);
  }, 100);
};

// Show delete option when clicking on own message
export const showDeleteOption = (messageId) => {
  // Remove existing menu
  const existingMenu = document.getElementById('deleteMenu');
  if (existingMenu) existingMenu.remove();
  
  const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
  if (!messageElement) return;
  
  const rect = messageElement.getBoundingClientRect();
  
  const menu = document.createElement('div');
  menu.id = 'deleteMenu';
  menu.style.cssText = `
    position: fixed;
    left: ${rect.left + rect.width / 2 - 60}px;
    top: ${rect.top - 50}px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.2);
    padding: 4px 0;
    z-index: 10003;
    min-width: 120px;
    animation: fadeIn 0.2s ease-out;
  `;
  
  menu.innerHTML = `
    <style>
      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    </style>
    <div 
      onclick="window.deleteMessage('${messageId}'); document.getElementById('deleteMenu').remove();"
      style="
        padding: 12px 16px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 12px;
        color: #ea4335;
        transition: background 0.2s;
      "
      onmouseover="this.style.background='#f1f3f4'"
      onmouseout="this.style.background='white'"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="3 6 5 6 21 6"></polyline>
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
      </svg>
      <span style="font-size: 14px; font-weight: 500;">Delete</span>
    </div>
  `;
  
  document.body.appendChild(menu);
  
  // Close menu when clicking outside
  setTimeout(() => {
    const closeHandler = (e) => {
      if (!menu.contains(e.target)) {
        menu.remove();
        document.removeEventListener('click', closeHandler);
      }
    };
    document.addEventListener('click', closeHandler);
  }, 100);
};

window.handleMessageTouchStart = handleMessageTouchStart;
window.handleMessageTouchEnd = handleMessageTouchEnd;
window.handleMessageClick = handleMessageClick;
window.showReactionsDetails = showReactionsDetails;
window.closeReactionsDetails = closeReactionsDetails;
window.handleMessageLongPress = handleMessageLongPress;
window.showDeleteMessageMenu = showDeleteMessageMenu;
window.startEditingMessage = startEditingMessage;
window.cancelEditingMessage = cancelEditingMessage;
window.confirmEditingMessage = confirmEditingMessage;
window.showEmojiReactionPicker = showEmojiReactionPicker;
window.toggleMessageTimestamp = toggleMessageTimestamp;
window.openChatAndClearIndicator = openChatAndClearIndicator;
window.loadChatUserStatuses = loadChatUserStatuses;
window.backToChatsListFromChat = backToChatsListFromChat;
window.openEventDetailsFromChat = openEventDetailsFromChat;
window.openEventChat = openEventChat;
window.closeChatPanel = closeChatPanel;
window.cleanupAllSubscriptions = cleanupAllSubscriptions;
window.handleChatScroll = handleChatScroll;
window.scrollToBottom = scrollToBottom;
window.toggleChatSettings = toggleChatSettings;
window.toggleChatEnabled = toggleChatEnabled;
window.loadMessages = loadMessages;
window.sendMessage = sendMessage;
window.markMessageAsDelivered = markMessageAsDelivered;
window.markMessagesAsDelivered = markMessagesAsDelivered;
window.markMessagesAsRead = markMessagesAsRead;
window.markSingleMessageAsRead = markSingleMessageAsRead;
window.updateChatRow = updateChatRow;
window.setupChatRealtimeSubscription = setupChatRealtimeSubscription;
window.setupGlobalMessageListener = setupGlobalMessageListener;
window.toggleEmojiPicker = toggleEmojiPicker;
window.insertEmoji = insertEmoji;
window.showMessageOptions = showMessageOptions;
window.showReactionPicker = showReactionPicker;
window.addReaction = addReaction;
window.startReactionHover = startReactionHover;
window.cancelReactionHover = cancelReactionHover;
window.startReactionLongPress = startReactionLongPress;
window.cancelReactionLongPress = cancelReactionLongPress;
window.updateReactionsUI = updateReactionsUI;
window.editMessage = editMessage;
window.cancelEditMessage = cancelEditMessage;
window.saveEditMessage = saveEditMessage;
window.deleteMessage = deleteMessage;
window.showReactionPickerInline = showReactionPickerInline;
window.showDeleteOption = showDeleteOption;